import { buildClient, resolveSiteUrl, type WPClient } from "./wpClient";
import type { SeoPlugin, ConnectionInfo } from "../types";
import { showConfirm } from "./dialog";
import { getAnthropicKey } from "./apiKeys";
import { fetchLivePageSchema } from "./authClient";
import { applyTagPolicy } from "./tagPolicy";

// Connect to a WordPress site and describe it: resolve the canonical URL, test the app-password
// auth, and detect the SEO plugin, page builder, site title and whether SEO meta is REST-writable.
// Shared by the Connect button AND the on-refresh auto-reconnect so both take the exact same path.
export async function describeConnection(rawUrl: string, username: string, appPassword: string): Promise<ConnectionInfo> {
  // Resolve the entered URL to its canonical origin first (www/non-www, http/https) so a cross-
  // origin redirect can't strip the auth header. Fall back to the entered URL if resolution fails.
  const siteUrl = (await resolveSiteUrl(rawUrl)) || rawUrl;
  const client = buildClient(siteUrl, username, appPassword);
  const auth = await client.testAuth();
  if (!auth.ok) return { ok: false, error: auth.error ?? "Auth failed" };
  // Multisite gotcha: an application password authenticates against the whole NETWORK, so
  // users/me succeeds even when the account isn't a member of THIS subsite. In that case the
  // account has no role here (empty `roles`) and every content read/edit silently returns
  // nothing. Flag it so we can warn instead of leaving the user with mysterious empty results.
  // IMPORTANT: only flag when roles is PRESENT and empty — if the field is absent (some
  // configs/security layers strip it even in edit context) treat it as unknown, never warn.
  const noRoleOnSite = Array.isArray(auth.user?.roles) && auth.user!.roles.length === 0;
  const [plugin, siteTitle, builder] = await Promise.all([client.detectPlugin(), client.getSiteTitle(), client.detectPageBuilder()]);
  const keys = metaKeysFor(plugin);
  const metaWritable = plugin === "aioseo"
    ? await client.verifyAioseoMetaWritable()
    : keys ? await client.verifyMetaWritable(keys.title) : false;
  return { ok: true, user: auth.user?.name, plugin, builder, siteTitle, metaWritable, siteUrl, noRoleOnSite };
}

// Wrap top-level HTML elements in Gutenberg block comments so content created by the
// tool lands as real, editable blocks on block-editor sites. (On Classic-editor sites
// we send the plain HTML instead — see execCreatePageFromForm.)
export function htmlToGutenbergBlocks(html: string): string {
  if (!html.trim()) return "";
  const doc = new DOMParser().parseFromString(`<div id="__b">${html}</div>`, "text/html");
  const root = doc.getElementById("__b");
  if (!root) return html;
  const blocks: string[] = [];
  root.childNodes.forEach(node => {
    if (node.nodeType === Node.TEXT_NODE) {
      const t = (node.textContent ?? "").trim();
      if (t) blocks.push(`<!-- wp:paragraph --><p>${t}</p><!-- /wp:paragraph -->`);
      return;
    }
    if (node.nodeType !== Node.ELEMENT_NODE) return;
    const elx = node as HTMLElement;
    const tag = elx.tagName.toLowerCase();
    const outer = elx.outerHTML;
    if (/^h[1-6]$/.test(tag)) {
      const level = tag[1];
      blocks.push(`<!-- wp:heading${level === "2" ? "" : ` {"level":${level}}`} -->${outer}<!-- /wp:heading -->`);
    } else if (tag === "p")          blocks.push(`<!-- wp:paragraph -->${outer}<!-- /wp:paragraph -->`);
    else if (tag === "ul")           blocks.push(`<!-- wp:list -->${outer}<!-- /wp:list -->`);
    else if (tag === "ol")           blocks.push(`<!-- wp:list {"ordered":true} -->${outer}<!-- /wp:list -->`);
    else if (tag === "blockquote")   blocks.push(`<!-- wp:quote -->${outer}<!-- /wp:quote -->`);
    else                             blocks.push(`<!-- wp:html -->${outer}<!-- /wp:html -->`);
  });
  return blocks.join("\n\n");
}

export type FixResult = {
  ok: boolean;
  message: string;
  manual?: boolean;
  // Set when a schema write was dropped because the SEO Audit helper isn't active
  // (e.g. it was removed by a theme switch). The app uses this to clear its cached
  // "schema ready" flag and re-open the setup dialog.
  needsSchemaHelper?: boolean;
  // Set for new-page creations so the success toast can show the page title (blue)
  // and a clickable link to the live page.
  createdTitle?: string;
  createdUrl?: string;
  // Schema status for a new-page creation (e.g. "Schema added (Article, FAQPage)").
  createdSchema?: string;
  // The schema @types added, so the success toast can list them as vertical bullets
  // (instead of the parenthetical string). Only set for the clean "Schema added" case.
  createdSchemaTypes?: string[];
  // Set when a homepage/business enhancement can't be auto-applied without duplicating an existing
  // business entity. Carries the pretty-printed MERGED block (existing schema + the recommended
  // enhancements, de-duplicated) for the user to paste over their original block by hand.
  mergedSchema?: string;
  // History logging: the affected page URL + WordPress id, set ONLY on a real (non-noop) WP write by
  // execSchemaFix / execInternalLinkFix / execPagesMetaFix. The App reads these at the apply-success
  // branch to record an activity_log entry. (Creates use createdUrl instead.) Left unset on no-ops so
  // "already linked" / "nothing to add" outcomes are never logged.
  targetUrl?: string;
  pageId?: number;
};

// The SEO plugin detected on connect — set at the start of each executeFixById
// call so the create/update helpers write the correct meta keys.
let activeSeoPlugin: SeoPlugin = "unknown";

// REST-writable meta keys per SEO plugin. Returns null for plugins whose SEO
// fields are NOT writable via the core REST `meta` field (AIOSEO uses custom
// tables; "unknown" = couldn't detect), so we can warn instead of guessing.
export function metaKeysFor(plugin: SeoPlugin): { title: string; description: string } | null {
  switch (plugin) {
    case "rankmath": return { title: "rank_math_title",        description: "rank_math_description" };
    case "yoast":    return { title: "_yoast_wpseo_title",     description: "_yoast_wpseo_metadesc" };
    case "seopress": return { title: "_seopress_titles_title", description: "_seopress_titles_desc" };
    default:         return null; // aioseo / unknown
  }
}

// REST-writable canonical-URL meta key per SEO plugin (registered show_in_rest by the LSG
// plugin v1.12.0+). Returns null for AIOSEO (canonical lives in its own table — written via
// saveAioseoMeta) and "unknown" (couldn't detect).
export function canonicalKeyFor(plugin: SeoPlugin): string | null {
  switch (plugin) {
    case "rankmath": return "rank_math_canonical_url";
    case "yoast":    return "_yoast_wpseo_canonical";
    case "seopress": return "_seopress_robots_canonical";
    default:         return null; // aioseo / unknown
  }
}

// Write the SEO meta title/description the right way for the active plugin: RankMath/Yoast/
// SEOPress store them in core post meta; AIOSEO keeps them in its own table and is written via
// the LSG plugin's dedicated route. Used by every meta-writing path (audit tabs, Express Meta
// Data, and the hardcoded All-Fixes meta fixes) so all SEO plugins behave consistently.
async function writeSeoMeta(
  client: WPClient,
  pageId: number,
  fields: { title?: string; description?: string },
): Promise<{ ok: boolean; error?: string }> {
  const title = fields.title?.trim();
  const description = fields.description?.trim();
  if (!title && !description) return { ok: true };
  if (activeSeoPlugin === "aioseo") {
    const body: { title?: string; description?: string } = {};
    if (title) body.title = title;
    if (description) body.description = description;
    const r = await client.saveAioseoMeta(pageId, body);
    return r.success ? { ok: true } : { ok: false, error: r.error ?? undefined };
  }
  const keys = metaKeysFor(activeSeoPlugin);
  if (!keys) return { ok: false, error: `the "${activeSeoPlugin}" SEO plugin isn't writable via the REST API` };
  const metaBody: Record<string, string> = {};
  if (title) metaBody[keys.title] = title;
  if (description) metaBody[keys.description] = description;
  const r = await client.updatePage(pageId, { meta: metaBody });
  return r.success ? { ok: true } : { ok: false, error: r.error ?? undefined };
}

// Page IDs for testing.teenproof.com — matches CLAUDE.md live IDs
const PID = {
  homepage:  2731,
  tat:      12760,
  detox:     8807,
  res:       9141,
  php_add:   9334,
  iop_add:   9335,
  php_mh:    9247,
  iop_mh:    9248,
  mh_hub:   14127,
  vc:        8745,
  faq:       9503,
};

// Resolve the WP page ID for a parent page entered in the New Page Details form — looked up
// LIVE on the connected site by slug, so it works for any site (no hardcoded page IDs).
// Accepts either a bare slug or a full URL (uses the last path segment as the slug).
// Returns {} for a blank field (→ publish at root), { id } on a match, or { error } when a
// parent was entered but no page with that slug exists (so we never silently misplace it).
async function resolveParentId(client: WPClient, parentInput: string): Promise<{ id?: number; error?: string }> {
  const raw = (parentInput || "").trim();
  if (!raw) return {}; // blank → root
  let pslug = raw;
  try {
    if (/^https?:\/\//i.test(raw) || raw.includes("/")) {
      const path = /^https?:\/\//i.test(raw) ? new URL(raw).pathname : raw;
      pslug = path.replace(/[#?].*$/, "").replace(/\/+$/, "").split("/").filter(Boolean).pop() ?? raw;
    }
  } catch { /* keep raw */ }
  pslug = pslug.toLowerCase().trim();
  if (!pslug) return {};
  const found = await client.getPageBySlug(pslug).catch(() => null);
  if (found?.id) return { id: found.id };
  return { error: `Parent page "${raw}" wasn't found on your site. Enter the exact slug (or URL) of an existing parent page, or leave the Parent Page field blank to publish at the root.` };
}

// ── Helpers ───────────────────────────────────────────────────────

function schemaBlock(schema: object | object[]): string {
  return `\n\n<!-- SEO Audit Schema -->\n<script type="application/ld+json">\n${JSON.stringify(schema, null, 2)}\n</script>`;
}

async function appendSchema(client: WPClient, pageId: number, schema: object | object[]): Promise<FixResult> {
  const content = await client.getPageContent(pageId);
  if (!content) return { ok: false, message: "Could not fetch page content — verify connection" };
  const typeLabel = Array.isArray(schema)
    ? (schema as {["@type"]:string}[]).map(s => s["@type"]).join(", ")
    : (schema as {["@type"]:string})["@type"];
  if (content.raw.includes("SEO Audit Schema")) {
    // Check if this specific type already present
    if (content.raw.includes(`"@type": "${Array.isArray(schema) ? (schema as {["@type"]:string}[])[0]["@type"] : (schema as {["@type"]:string})["@type"]}"`)) {
      return { ok: true, message: `Schema (${typeLabel}) already injected on this page` };
    }
  }
  const result = await client.updatePage(pageId, { content: { raw: content.raw + schemaBlock(schema) } });
  return result.success
    ? { ok: true, message: `Schema injected: ${typeLabel}` }
    : { ok: false, message: `Page update failed: ${result.error}` };
}

async function execMetaTitle(client: WPClient, pageId: number, title: string): Promise<FixResult> {
  const r = await writeSeoMeta(client, pageId, { title });
  return r.ok
    ? { ok: true, message: `Meta title updated → "${title}"` }
    : { ok: false, message: `Update failed: ${r.error}` };
}

function escRe(s: string) { return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"); }

async function execH1(client: WPClient, pageId: number, oldH1: string, newH1: string): Promise<FixResult> {
  const content = await client.getPageContent(pageId);
  if (!content) return { ok: false, message: "Could not fetch page content — verify connection" };
  const pattern = new RegExp(`(<h1[^>]*>)${escRe(oldH1)}(</h1>)`, "i");
  if (!pattern.test(content.raw)) {
    return {
      ok: false,
      manual: true,
      message: `H1 not found in block editor content (page likely uses Elementor). Manual fix: change H1 to "${newH1}" in Elementor editor.`,
    };
  }
  const newContent = content.raw.replace(pattern, `$1${newH1}$2`);
  const result = await client.updatePage(pageId, { content: { raw: newContent } });
  return result.success
    ? { ok: true, message: `H1 updated → "${newH1}"` }
    : { ok: false, message: `Update failed: ${result.error}` };
}

function getTagsFromTextarea(id: string): string[] {
  const el = document.getElementById(id) as HTMLTextAreaElement | null;
  return (el?.value ?? "")
    .split(",")
    .map(t => t.trim().replace(/&amp;/g, "&"))
    .filter(Boolean);
}

// A heading reads as an FAQ question if it ends with "?" or opens with a question word.
function isQuestionHeading(t: string): boolean {
  const s = t.trim();
  return /\?\s*$/.test(s) || /^(who|what|when|where|why|how|do|does|can|are|is)\b/i.test(s);
}

// Concise answer for FAQ schema: a few useful sentences summarizing the page content.
// Pulls the opening body paragraph(s) and trims to the first ~maxSentences sentences.
function summarizeForAnswer(html: string, maxSentences = 3, maxChars = 600): string {
  try {
    const doc = new DOMParser().parseFromString(html, "text/html");
    const paras = Array.from(doc.body.querySelectorAll("p"))
      .map(p => (p.textContent ?? "").trim())
      .filter(Boolean);
    let text = "";
    for (const p of paras) {
      text = text ? `${text} ${p}` : p;
      if (text.length >= 240) break; // enough material to summarize
    }
    if (!text) text = (doc.body.textContent ?? "").replace(/\s+/g, " ").trim();
    const sentences = text.match(/[^.!?]+[.!?]+/g) ?? (text ? [text] : []);
    let out = sentences.slice(0, maxSentences).join(" ").trim();
    if (out.length > maxChars) out = `${out.slice(0, maxChars).replace(/\s+\S*$/, "")}…`;
    return out;
  } catch { return ""; }
}

// Pull FAQ Q&A pairs out of generated page HTML.
//  • Default: find the "Frequently Asked Questions" H2, then pair each following H3
//    (question) with the text beneath it (answer), up to the next H2.
//  • faqPage = true (the whole page is an FAQ): scan the entire body and treat any
//    question-style H2/H3 as a question, with the text beneath it as the answer.
function extractFaqs(html: string, faqPage = false): { q: string; a: string }[] {
  try {
    const doc = new DOMParser().parseFromString(html, "text/html");
    let nodes: Element[];
    let isQuestion: (el: Element) => boolean;
    if (faqPage) {
      nodes = Array.from(doc.body.children);
      isQuestion = el => (el.tagName === "H2" || el.tagName === "H3") && isQuestionHeading(el.textContent ?? "");
    } else {
      const h2s = Array.from(doc.body.querySelectorAll("h2"));
      const faqH2 = h2s.find(h => /frequently asked questions|faqs?\b/i.test((h.textContent ?? "").trim()));
      if (faqH2) {
        nodes = [];
        let n = faqH2.nextElementSibling;
        while (n && n.tagName !== "H2") { nodes.push(n); n = n.nextElementSibling; }
      } else {
        nodes = Array.from(doc.body.children);
      }
      isQuestion = el => el.tagName === "H3";
    }
    const faqs: { q: string; a: string }[] = [];
    let cur: { q: string; a: string } | null = null;
    for (const el of nodes) {
      if (isQuestion(el)) {
        if (cur && cur.a.trim()) faqs.push(cur);
        cur = { q: (el.textContent ?? "").trim(), a: "" };
      } else if (cur) {
        const t = (el.textContent ?? "").trim();
        if (t) cur.a += (cur.a ? " " : "") + t;
      }
    }
    if (cur && cur.a.trim()) faqs.push(cur);
    return faqs.filter(f => f.q && f.a);
  } catch { return []; }
}

// Build JSON-LD blocks for a newly created page: a primary Article (articles) or
// WebPage (service/landing) block, plus a FAQPage block when the body has an FAQ
// section. Client-agnostic — only the title, description, URL, and brand are used.
function buildPageSchema(opts: {
  pageType?: "service" | "article"; title: string; description?: string;
  url?: string; brand?: string; rawHtml: string; isFaq?: boolean; faqAnswer?: string;
}): string[] {
  const faqBlock = (faqs: { q: string; a: string }[]): Record<string, unknown> => ({
    "@context": "https://schema.org",
    "@type": "FAQPage",
    ...(opts.url ? { "@id": `${opts.url}#faq` } : {}),
    mainEntity: faqs.map(f => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  });

  // FAQ-topic page → FAQPage covering EVERY Q&A on the page, not just the page title:
  //  • the page's own question (title) + its answer (the generated answer, else a summary)
  //  • PLUS every question/answer found inside the generated content (analyzed below)
  // De-duplicated by question so a repeated title question isn't listed twice.
  if (opts.isFaq) {
    const faqs: { q: string; a: string }[] = [];
    const question = opts.title.replace(/\s*[|–—].*$/, "").trim(); // drop any brand suffix
    const answer = (opts.faqAnswer ?? "").trim() || summarizeForAnswer(opts.rawHtml);
    if (question && answer) faqs.push({ q: question, a: answer });
    faqs.push(...extractFaqs(opts.rawHtml, true)); // scan the whole body for Q&A pairs
    const seen = new Set<string>();
    const deduped = faqs.filter(f => {
      const k = f.q.toLowerCase().replace(/\s+/g, " ").trim();
      if (!k || seen.has(k)) return false;
      seen.add(k);
      return true;
    });
    if (deduped.length) return [JSON.stringify(faqBlock(deduped))];
    // No usable Q&As — fall through to the generic page schema below.
  }

  const isArticle = opts.pageType === "article";
  const primary: Record<string, unknown> = {
    "@context": "https://schema.org",
    "@type": isArticle ? "Article" : "WebPage",
    ...(isArticle ? { headline: opts.title } : { name: opts.title }),
  };
  if (opts.description) primary.description = opts.description;
  if (opts.url) { primary.url = opts.url; primary["@id"] = opts.url; }
  if (opts.brand) {
    primary.publisher = { "@type": "Organization", name: opts.brand };
    if (isArticle) primary.author = { "@type": "Organization", name: opts.brand };
  }
  const blocks: Record<string, unknown>[] = [primary];

  // Non-FAQ page with a "Frequently Asked Questions" section → add a FAQPage block too.
  const faqs = extractFaqs(opts.rawHtml);
  if (faqs.length) blocks.push(faqBlock(faqs));

  return blocks.map(b => JSON.stringify(b));
}

// Robust, complete JSON-LD for a Service Area / location page: a Service (with serviceType +
// areaServed City/State + provider LocalBusiness carrying the GBP profile in sameAs), a WebPage
// that points at that Service, a BreadcrumbList, and a FAQPage when the body has Q&As. Built at
// publish time so the real page URL can anchor every @id.
function buildServiceAreaSchema(opts: {
  ctx: { category?: string; city?: string; state?: string; businessName?: string; gbpUrl?: string; businessAddress?: string; services?: Array<{ name?: string; url?: string }> };
  title: string; description?: string; url?: string; brand?: string; rawHtml: string;
}): string[] {
  const { ctx } = opts;
  const url = (opts.url || "").trim();
  const siteRoot = (() => { try { return url ? new URL(url).origin : ""; } catch { return ""; } })();
  const businessName = (ctx.businessName || opts.brand || "").trim();
  const category = (ctx.category || "").trim();
  const city = (ctx.city || "").trim();
  const state = (ctx.state || "").trim();
  const name = opts.title.replace(/\s*[|–—].*$/, "").trim(); // drop any brand suffix

  const provider: Record<string, unknown> = { "@type": "LocalBusiness" };
  if (businessName) provider.name = businessName;
  if (siteRoot) provider.url = siteRoot;
  if (ctx.businessAddress) provider.address = { "@type": "PostalAddress", name: ctx.businessAddress };
  const sameAs = [ctx.gbpUrl].map(s => (s || "").trim()).filter(Boolean);
  if (sameAs.length) provider.sameAs = sameAs;

  // The user's Service rows → an OfferCatalog on the provider: one Offer per service, each
  // wrapping a Service (name + url) scoped to the city this page serves.
  const services = (ctx.services || [])
    .map(s => ({ name: (s.name || "").trim(), url: (s.url || "").trim() }))
    .filter(s => s.name || s.url);
  if (services.length) {
    provider.hasOfferCatalog = {
      "@type": "OfferCatalog",
      name: `${(category || businessName || "").trim()} Services`.trim(),
      itemListElement: services.map(s => ({
        "@type": "Offer",
        itemOffered: {
          "@type": "Service",
          ...(s.name ? { name: s.name } : {}),
          ...(s.url ? { url: s.url } : {}),
          ...(city ? { areaServed: { "@type": "City", name: city } } : {}),
        },
      })),
    };
  }

  const service: Record<string, unknown> = { "@context": "https://schema.org", "@type": "Service" };
  if (url) service["@id"] = `${url}#service`;
  service.name = name;
  if (category) service.serviceType = category;
  if (opts.description) service.description = opts.description;
  if (url) service.url = url;
  if (Object.keys(provider).length > 1) service.provider = provider;
  if (city) {
    service.areaServed = {
      "@type": "City", name: city,
      ...(state ? { containedInPlace: { "@type": "State", name: state } } : {}),
    };
  }

  const webpage: Record<string, unknown> = { "@context": "https://schema.org", "@type": "WebPage" };
  if (url) { webpage["@id"] = url; webpage.url = url; }
  webpage.name = opts.title;
  if (opts.description) webpage.description = opts.description;
  if (url) webpage.about = { "@id": `${url}#service` };
  if (businessName) webpage.publisher = { "@type": "Organization", name: businessName };

  const blocks: Record<string, unknown>[] = [service, webpage];

  if (siteRoot && url) {
    blocks.push({
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: siteRoot },
        { "@type": "ListItem", position: 2, name, item: url },
      ],
    });
  }

  const faqs = extractFaqs(opts.rawHtml);
  if (faqs.length) {
    blocks.push({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      ...(url ? { "@id": `${url}#faq` } : {}),
      mainEntity: faqs.map(f => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
    });
  }

  return blocks.map(b => JSON.stringify(b));
}

async function execCreatePage(
  client: WPClient,
  opts: {
    title: string; slug: string; content: string; tags: string[]; parent?: number;
    isPost?: boolean; meta?: Record<string, string>;
    // Category term IDs to file a Post under (Post or Page selector = "Post").
    categories?: number[];
    // Site-local ISO datetime (YYYY-MM-DDTHH:MM:SS) to schedule/backdate the publish.
    // Null/omitted → publish immediately.
    publishDate?: string | null;
    // When provided, JSON-LD schema is generated from this and written to the new
    // page's `_audit_schema` meta (printed in <head> by the SEO Audit helper).
    schemaInput?: { pageType?: "service" | "article"; rawHtml: string; brand?: string; description?: string; isFaq?: boolean; faqAnswer?: string; location?: string;
      // When present, the page is a Service Area / location page → build robust service-area
      // JSON-LD (Service + areaServed + provider) instead of the generic WebPage schema.
      serviceArea?: { category?: string; city?: string; state?: string; businessName?: string; gbpUrl?: string; businessAddress?: string; services?: Array<{ name?: string; url?: string }> } };
    // Raw SEO meta title/description. For RankMath/Yoast/SEOPress these are already set via
    // `meta` above; this is used to write AIOSEO (its own table) AFTER the page is created.
    seoMeta?: { title?: string; description?: string };
  }
): Promise<FixResult> {
  // Duplicate guard — don't accidentally re-create a page that already exists on the
  // site (e.g. the same suggestion published during an earlier crawl/session). Checks
  // the LIVE site by slug + title across pages AND posts; if a match is found, the user
  // must explicitly confirm publishing a duplicate. Best-effort: a lookup error never
  // blocks creation.
  try {
    const dup = await client.findExistingByTitleOrSlug({ slug: opts.slug, title: opts.title });
    if (dup) {
      const toolLine = dup.createdByTool
        ? `\n\nThis page was created by this tool${/^\d{4}-\d{2}-\d{2}/.test(dup.createdByTool) ? ` on ${dup.createdByTool.slice(0, 10)}` : ""}.`
        : "";
      const proceed = await showConfirm(
        `A ${dup.type} that matches this ${dup.matchedBy === "slug" ? "URL slug" : "title"} already exists on the site:\n\n` +
        `"${dup.title || opts.title}"\n${dup.link}${toolLine}\n\n` +
        `It looks like this page may already have been created (for example, during a previous crawl). ` +
        `Publishing now will create a SEPARATE duplicate page. Publish a duplicate anyway?`,
        { title: "Possible Duplicate Page", confirmLabel: "Publish Duplicate", cancelLabel: "Cancel", tone: "danger" },
      );
      if (!proceed) {
        return { ok: false, manual: true, message: `Skipped — a ${dup.type} titled "${dup.title || opts.title}" already exists at ${dup.link}. No duplicate was created.` };
      }
    }
  } catch { /* lookup failed — proceed with creation rather than block the user */ }

  // Mark every tool-created page: a visible "LSG Tool" tag (easy reference in WP admin)
  // plus a hidden _lsg_tool_created meta key (durable — survives tag edits, used by the
  // duplicate check and any future "created by the tool" view).
  const TOOL_TAG = "LSG Tool";
  const tagsWithMarker = opts.tags.some(t => t.trim().toLowerCase() === TOOL_TAG.toLowerCase())
    ? opts.tags
    : [...opts.tags, TOOL_TAG];
  // White-label accounts can hide the "LSG …" marker tags from the client's WordPress. We still
  // derive the service-area marker (below) from the REQUESTED tags — so the durable _lsg_* meta is
  // stamped either way — and only drop the visible marker tags from what actually gets written.
  const tagIds = await client.getOrCreateTags(applyTagPolicy(tagsWithMarker));
  const body: Record<string, unknown> = {
    title: opts.title,
    slug: opts.slug,
    content: opts.content,
    tags: tagIds,
    status: "publish",
  };
  if (opts.parent) body.parent = opts.parent;
  if (opts.categories?.length) body.categories = opts.categories;
  // Publish date: blank → publish now. A FUTURE date schedules the page (status "future");
  // a PAST date backdates it (stays published). WordPress uses site-local time here.
  let scheduled = false;
  if (opts.publishDate) {
    const when = new Date(opts.publishDate).getTime();
    body.date = opts.publishDate;
    if (!isNaN(when) && when > Date.now() + 60_000) { body.status = "future"; scheduled = true; }
  }
  // Durable service-area marker: when the page carries the "LSG Service Area Page" tag (added by
  // the Service Area Buildout tab), also stamp _lsg_service_area meta so the tool's history can
  // find it even if that tag is later removed in WordPress.
  const isServiceArea = tagsWithMarker.some(t => t.trim().toLowerCase() === "lsg service area page");
  const nowIso = new Date().toISOString();
  body.meta = { ...(opts.meta ?? {}), _lsg_tool_created: nowIso, ...(isServiceArea ? { _lsg_service_area: nowIso } : {}) };
  const result = opts.isPost
    ? await client.createPost(body)
    : await client.createPage(body);
  if (!result.success) return { ok: false, message: `Publish failed: ${result.error}` };
  const newId = (result.data as { id?: number })?.id;
  const link  = (result.data as { link?: string })?.link;

  // Add structured-data schema to the new page (via the SEO Audit helper's _audit_schema
  // meta — printed in <head>, builder-agnostic). Non-fatal: the page is already live.
  let schemaNote = "";
  let schemaTypes: string[] = [];
  if (opts.schemaInput && newId) {
    try {
      // Prefer AI analysis of the generated CONTENT (detects the real page type — service /
      // article / how-to / etc. — and captures EVERY FAQ). Falls back to the deterministic
      // builder if there's no Anthropic key or the AI call fails/returns nothing.
      let blocks: string[] = [];
      // Service Area / location pages get purpose-built, complete service-area JSON-LD
      // (Service + areaServed + provider) — deterministic, so it's guaranteed regardless of
      // whether an AI key is present. This takes precedence over the generic page schema.
      if (opts.schemaInput.serviceArea) {
        blocks = buildServiceAreaSchema({
          ctx:         opts.schemaInput.serviceArea,
          title:       opts.title,
          description: opts.schemaInput.description,
          url:         link,
          brand:       opts.schemaInput.brand,
          rawHtml:     opts.schemaInput.rawHtml,
        });
      }
      const aiKey = getAnthropicKey();
      if (!blocks.length && aiKey) {
        try {
          const res = await fetch("/api/generate-page-schema", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              title:       opts.title,
              html:        opts.schemaInput.rawHtml,
              url:         link,
              description: opts.schemaInput.description,
              brand:       opts.schemaInput.brand,
              location:    opts.schemaInput.location,
              anthropicKey: aiKey,
            }),
          });
          const data = await res.json() as { ok?: boolean; blocks?: Record<string, unknown>[] };
          if (data.ok && Array.isArray(data.blocks) && data.blocks.length) {
            blocks = data.blocks.map(b => JSON.stringify(b));
          }
        } catch { /* fall back to the deterministic builder below */ }
      }
      if (!blocks.length) {
        blocks = buildPageSchema({
          pageType:    opts.schemaInput.pageType,
          title:       opts.title,
          description: opts.schemaInput.description,
          url:         link,
          brand:       opts.schemaInput.brand,
          rawHtml:     opts.schemaInput.rawHtml,
          isFaq:       opts.schemaInput.isFaq,
          faqAnswer:   opts.schemaInput.faqAnswer,
        });
      }
      if (blocks.length) {
        // Schema.org conformance guard — the SAME check the Schema / Express Schema tabs run at
        // write time (execSchemaFix). Runs on ALL creation schema (service-area, AI, and the
        // deterministic fallback) so nothing is published without validation: fix invalid enums,
        // strip invented properties, flag unknown types. Blocks are JSON strings here, so parse →
        // validate → re-stringify. Fail-open: if validation is unavailable, write the built blocks.
        try {
          const parsedBlocks = blocks
            .map(b => { try { return JSON.parse(b) as Record<string, unknown>; } catch { return null; } })
            .filter((b): b is Record<string, unknown> => !!b);
          if (parsedBlocks.length) {
            const vres = await fetch("/api/validate-schema", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ blocks: parsedBlocks }) });
            const vdata = await vres.json() as { ok?: boolean; blocks?: Record<string, unknown>[] };
            if (vdata.ok && Array.isArray(vdata.blocks) && vdata.blocks.length) blocks = vdata.blocks.map(b => JSON.stringify(b));
          }
        } catch { /* fail-open — write the built blocks as-is */ }
        const wpType = opts.isPost ? "posts" : "pages";
        const r = await client.setAuditSchema(newId, blocks, wpType);
        if (r.success) {
          const after = await client.getAuditSchema(newId, wpType);
          if (after.length) {
            schemaTypes = blocks.map(b => { try { return schemaType(JSON.parse(b)); } catch { return ""; } }).filter(Boolean);
            schemaNote = ` Schema added (${schemaTypes.join(", ")}).`;
          } else {
            schemaNote = " (Local SEO Garage (LSG) plugin not active — page published without schema; install the plugin, then add schema from the Schema tab.)";
          }
        }
      }
    } catch { schemaNote = " (Schema could not be written — page published; add schema from the Schema tab.)"; }
  }

  // AIOSEO keeps the SEO title/description in its own table, so they can't be set through the
  // create call's `meta` (that path handles RankMath/Yoast/SEOPress). Write them now that the
  // page exists, via the LSG plugin route. Non-fatal: the page is already live.
  let metaNote = "";
  if (newId && activeSeoPlugin === "aioseo" && (opts.seoMeta?.title || opts.seoMeta?.description)) {
    const r = await writeSeoMeta(client, newId, opts.seoMeta);
    if (!r.ok) metaNote = ` (AIOSEO meta title/description not set: ${r.error})`;
  }

  if (scheduled) {
    const whenLabel = (opts.publishDate ?? "").replace("T", " ").slice(0, 16);
    return {
      ok: true,
      message: `Scheduled (ID ${newId ?? "?"}) — "${opts.title}" will publish on ${whenLabel}.${schemaNote}${metaNote}`,
      createdSchema: schemaNote.trim() || undefined,
      createdSchemaTypes: schemaTypes.length ? schemaTypes : undefined,
    };
  }

  return {
    ok: true,
    message: `Published (ID ${newId ?? "?"}) — "${opts.title}" is now live${link ? ` at ${link}` : ""}.${schemaNote}${metaNote}`,
    createdTitle: opts.title,
    createdUrl: link,
    createdSchema: schemaNote.trim() || undefined,
    createdSchemaTypes: schemaTypes.length ? schemaTypes : undefined,
  };
}

function readCreateForm(fixId: string) {
  const val = (id: string) =>
    ((document.getElementById(id) as HTMLInputElement | HTMLTextAreaElement | null)?.value ?? "").trim();
  const pageTitle  = val(`cft-title-${fixId}`);
  const metaTitle  = val(`cft-mt-${fixId}`);
  const slug       = val(`cft-sl-${fixId}`);
  const tags = val(`cft-pt-${fixId}`)
    .split(",").map(t => t.trim().replace(/&amp;/g, "&")).filter(Boolean);
  const parentSlug = val(`cft-par-${fixId}`);
  // Explicit Post/Page selector (injected by AuditSection) wins; otherwise fall
  // back to inferring from the tags (Article → post, InnerPage → page).
  const typeSel    = (document.getElementById(`cft-type-${fixId}`) as HTMLSelectElement | null)?.value;
  const isPost     = typeSel === "post" ? true
                   : typeSel === "page" ? false
                   : (tags.includes("Article") && !tags.includes("InnerPage"));
  return {
    pageTitle:  pageTitle || metaTitle,
    metaTitle,
    metaDesc:   val(`cft-md-${fixId}`),
    slug,
    tags,
    parentSlug,
    isPost,
    publishDate: val(`cft-pd-${fixId}`),
  };
}

// Parse a "YYYY-MM-DD HH:MM" (or ISO) publish date into a site-local ISO string WordPress
// accepts. Returns null when blank/invalid (→ publish immediately).
function parsePublishDate(s: string): string | null {
  const t = (s || "").trim();
  if (!t) return null;
  const m = t.match(/^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})/);
  if (m) return `${m[1]}-${m[2]}-${m[3]}T${m[4]}:${m[5]}:00`;
  const d = new Date(t);
  return isNaN(d.getTime()) ? null : `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}T${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}:00`;
}

async function execCreatePageFromForm(
  client: WPClient,
  fixId: string,
  contentFn: () => string
): Promise<FixResult> {
  const form = readCreateForm(fixId);
  if (!form.pageTitle) return { ok: false, message: "Page Title is empty — fill it in before creating." };

  // Service-area cards (fixId "sa-…", built by the Service Area Buildout tab) always publish
  // with the durable "LSG Service Area Page" tag — even if the user cleared it from the Page
  // Tags field — because that tag drives the _lsg_service_area meta stamp (execCreatePage) and
  // the tab's History list. InnerPage is deliberately never force-added.
  if (fixId.startsWith("sa-")) {
    const SA_TAG = "LSG Service Area Page";
    if (!form.tags.some(t => t.trim().toLowerCase() === SA_TAG.toLowerCase())) form.tags.push(SA_TAG);
  }

  // The shared field means different things by type (its label switches in the UI):
  //  • Page → a Parent Page (hierarchical nesting), resolved live by slug.
  //  • Post → a Category to file the post under (created on the site if it doesn't exist).
  let parentId: number | undefined;
  let categoryIds: number[] | undefined;
  if (form.isPost) {
    const cat = (form.parentSlug || "").trim();
    if (cat) {
      const catId = await client.getOrCreateCategory(cat).catch(() => null);
      if (!catId) return { ok: false, message: `Couldn't resolve or create the category "${cat}" on your site. Check the connection and try again.` };
      categoryIds = [catId];
    }
  } else {
    const parent = await resolveParentId(client, form.parentSlug);
    if (parent.error) return { ok: false, message: parent.error };
    parentId = parent.id;
  }
  const autoSlug = form.metaTitle.split(/[|–—]/)[0].trim()
    .toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "").slice(0, 60);

  // The Generated Content editor is a contenteditable rich-text field — its innerHTML
  // IS the page HTML. Format it for the site's editor: Gutenberg block markup on block-
  // editor sites (so it lands as real blocks), or plain HTML on Classic-editor sites.
  // Falls back to the static template when nothing was generated.
  const editorEl = document.getElementById(`cft-content-${fixId}`) as HTMLElement | null;
  const editedHtml = (editorEl?.innerHTML ?? "").trim();
  // Content-derived FAQ answer stashed at generation time (used for FAQPage schema).
  const faqAnswer = editorEl?.dataset.faqAnswer?.trim() || undefined;
  const rawHtml = editedHtml || contentFn();
  const editorType = await client.detectEditor();
  const content = editorType === "block" ? htmlToGutenbergBlocks(rawHtml) : rawHtml;

  // SEO meta for RankMath/Yoast/SEOPress goes into the create call's `meta`; AIOSEO is written
  // after creation via `seoMeta` (its data lives in a separate table, not post meta).
  const keys = metaKeysFor(activeSeoPlugin);
  const meta: Record<string, string> = {};
  if (keys) {
    meta[keys.title]       = form.metaTitle || form.pageTitle;
    meta[keys.description] = form.metaDesc;
  }
  const seoMeta = { title: form.metaTitle || form.pageTitle, description: form.metaDesc };

  // Schema generation context: article vs service drives Article vs WebPage schema.
  const pageType: "service" | "article" =
    (form.isPost || (form.tags.includes("Article") && !form.tags.includes("InnerPage"))) ? "article" : "service";
  const brand = (localStorage.getItem("cover_business_name") ?? "").trim() || undefined;

  // FAQ page → FAQPage schema. Determined from the page TITLE: it contains "FAQ" or
  // begins with a question word (Who/What/When/Where/Why/How/Do/Does/Can/Are/Is) or
  // ends with a question mark.
  const isFaq = /faq/i.test(form.pageTitle) || isQuestionHeading(form.pageTitle);

  // Service Area / location pages carry a context payload (from buildServiceAreaCards) so we can
  // build robust service-area JSON-LD on publish. Prefer that; fall back to parsing the title
  // ("Category Near City, ST") + cover data for cards created before the payload existed.
  let serviceArea: { category?: string; city?: string; state?: string; businessName?: string; gbpUrl?: string; businessAddress?: string } | undefined;
  if (fixId.startsWith("sa-")) {
    try {
      const raw = document.getElementById(`sa-ctx-${fixId}`)?.textContent?.trim();
      if (raw) serviceArea = JSON.parse(raw);
    } catch { /* fall through to title parse */ }
    if (!serviceArea) {
      const m = form.pageTitle.replace(/\s*[|–—].*$/, "").trim().match(/^(.*?)\s+Near\s+(.+?),\s*([A-Za-z]{2})$/);
      serviceArea = {
        category: m?.[1]?.trim(),
        city: m?.[2]?.trim(),
        state: m?.[3]?.trim().toUpperCase(),
        businessName: brand,
        businessAddress: (localStorage.getItem("cover_full_address") ?? "").trim() || undefined,
      };
    }
  }

  return execCreatePage(client, {
    title:   form.pageTitle,
    slug:    form.slug || autoSlug,
    content,
    tags:    form.tags,
    parent:  parentId,        // Pages only
    categories: categoryIds,  // Posts only
    isPost:  form.isPost,
    meta,
    seoMeta,
    publishDate: parsePublishDate(form.publishDate),
    // Build schema from the clean (pre-Gutenberg) HTML so FAQ extraction is reliable.
    schemaInput: { pageType, rawHtml, brand, description: form.metaDesc, isFaq, faqAnswer, location: (localStorage.getItem("cover_location") ?? "").trim() || undefined, serviceArea },
  });
}

// ── Schema tab (sc-{slug}) — add JSON-LD to a page via the SEO Audit helper ──────
// Reads the machine-readable payload embedded in the card, resolves the WordPress
// page, and appends the recommended blocks to the page's `_audit_schema` meta. The
// helper prints them in <head>, so this works on any page builder and ADDS to —
// never replaces — schema already on the page. Idempotent: re-approving is a no-op.

function schemaType(o: unknown): string {
  const t = o && typeof o === "object" && "@type" in o ? (o as Record<string, unknown>)["@type"] : "";
  return Array.isArray(t) ? String(t[0] ?? "") : (t == null ? "" : String(t));
}
// ALL @type values on a block (a block may declare an array of types).
function schemaTypesOf(o: unknown): string[] {
  const t = o && typeof o === "object" && "@type" in o ? (o as Record<string, unknown>)["@type"] : undefined;
  if (Array.isArray(t)) return t.map(String);
  return t == null ? [] : [String(t)];
}
function schemaId(o: unknown): string {
  return o && typeof o === "object" && "@id" in o ? String((o as Record<string, unknown>)["@id"]) : "";
}
function normType(t: string): string {
  return t.replace(/^https?:\/\/schema\.org\//i, "").trim().toLowerCase();
}

// Union two arrays, de-duplicating by deep value (JSON identity). Used to combine array-valued
// schema properties (sameAs, areaServed, …) when merging an enhancement into an existing entity.
function unionArray(a: unknown[], b: unknown[]): unknown[] {
  const out = [...a];
  const seen = new Set(a.map(x => JSON.stringify(x)));
  for (const item of b) { const k = JSON.stringify(item); if (!seen.has(k)) { seen.add(k); out.push(item); } }
  return out;
}
// Deep-merge a recommended enhancement INTO an existing schema node. The existing node is the source
// of truth: identity (@type/@id/@context) is never changed, scalar conflicts keep the EXISTING value
// (so NAP — name/telephone/address — is never overwritten). The enhancement only fills in MISSING
// keys, unions arrays (sameAs, areaServed), and recurses into nested objects. Result = enriched
// existing node with no duplicated properties.
function deepMergeSchema(existing: Record<string, unknown>, add: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = { ...existing };
  for (const [k, v] of Object.entries(add)) {
    if (k === "@type" || k === "@id" || k === "@context") continue; // never touch identity
    const cur = out[k];
    if (cur === undefined || cur === null || cur === "") { out[k] = v; continue; } // add missing
    if (Array.isArray(cur)) { out[k] = unionArray(cur, Array.isArray(v) ? v : [v]); continue; } // union arrays
    if (Array.isArray(v)) { out[k] = unionArray([cur], v); continue; }
    if (cur && typeof cur === "object" && v && typeof v === "object") {
      out[k] = deepMergeSchema(cur as Record<string, unknown>, v as Record<string, unknown>); continue; // recurse
    }
    // scalar-vs-scalar conflict → keep the existing value (protects NAP/name/etc.)
  }
  return out;
}
// schema.org "business / organization" entity types — the top-level node that represents the
// business itself. A page should carry exactly ONE. Adding a second (even a different subtype,
// e.g. MedicalBusiness when MedicalClinic is already present) creates two competing entities and
// muddies NAP/entity signals — so the Schema tab never adds one when the page already has one.
// Curated to cover the industries the schema generator emits, plus a suffix rule for the long tail.
const BUSINESS_ENTITY_TYPES = new Set([
  "organization", "corporation", "localbusiness", "professionalservice", "medicalbusiness",
  "medicalclinic", "medicalorganization", "hospital", "physician", "dentist", "pharmacy",
  "restaurant", "foodestablishment", "cafeorcoffeeshop", "bar", "legalservice", "attorney", "notary",
  "plumber", "electrician", "hvacbusiness", "homeandconstructionbusiness", "roofingcontractor",
  "generalcontractor", "autorepair", "automotivebusiness", "beautysalon", "hairsalon", "dayspa",
  "healthandbeautybusiness", "realestateagent", "accountingservice", "financialservice", "insuranceagency",
  "exercisegym", "healthclub", "sportsactivitylocation", "educationalorganization", "school", "childcare",
  "store", "onlinestore", "shoppingcenter", "travelagency", "lodgingbusiness", "hotel", "veterinarycare",
  "emergencyservice", "governmentorganization", "ngo", "nightclub", "movingcompany", "selfstorage",
  "drycleaningorlaundry", "touristattraction",
]);
function isBusinessEntityType(t: string): boolean {
  const s = normType(t);
  if (!s) return false;
  if (BUSINESS_ENTITY_TYPES.has(s)) return true;
  return /(business|organization|corporation)$/.test(s); // long-tail LocalBusiness subtypes
}
function isBusinessEntityBlock(o: unknown): boolean {
  if (schemaTypesOf(o).some(isBusinessEntityType)) return true;
  const id = schemaId(o).toLowerCase();
  return id.includes("localbusiness") || id.includes("#organization");
}
function lastPathSlug(href: string): string {
  try {
    const u = new URL(/^https?:\/\//.test(href) ? href : `https://x.example${href.startsWith("/") ? "" : "/"}${href}`);
    return u.pathname.replace(/\/+$/, "").split("/").filter(Boolean).pop() ?? "";
  } catch {
    return href.replace(/[#?].*$/, "").replace(/\/+$/, "").split("/").filter(Boolean).pop() ?? "";
  }
}

// Normalize a page URL to a comparison key: host without www + path without a trailing slash,
// lowercased, hash/query dropped. Relative paths resolve against `siteRoot`.
function normPageUrl(u: string, siteRoot = ""): string {
  try {
    const abs = /^https?:\/\//i.test(u) ? u : (siteRoot ? new URL(u, siteRoot).href : u);
    const x = new URL(abs);
    return x.hostname.replace(/^www\./i, "").toLowerCase() + x.pathname.replace(/\/+$/, "").toLowerCase();
  } catch {
    return u.replace(/[#?].*$/, "").replace(/\/+$/, "").toLowerCase();
  }
}
// Real page URLs the crawl found this session (the page inventory), used to verify inner schema
// `url` fields without a network round-trip. Best-effort — empty if no crawl is stored.
function crawledPageUrls(): string[] {
  try {
    const raw = JSON.parse(localStorage.getItem("seo_full_metrics") ?? "null") as { pageMeta?: Array<{ url?: unknown }> } | null;
    return Array.isArray(raw?.pageMeta) ? raw!.pageMeta!.map(p => (typeof p.url === "string" ? p.url : "")).filter(Boolean) : [];
  } catch { return []; }
}
// Asset/media files (never a navigable page) — leave these `url`/image URLs untouched.
const ASSET_URL_RE = /\.(jpe?g|png|webp|gif|svg|ico|bmp|tiff?|avif|mp4|webm|mov|mp3|wav|pdf|docx?|xlsx?|pptx?|zip|css|js)(?:[?#]|$)/i;

// Verify every INTERNAL, navigable `url` inside a schema block against real pages, and STRIP any
// that doesn't resolve — so an Approve can never inject a fabricated/hallucinated page URL. A `url`
// is checked only when it's a same-site http(s) link that isn't an asset file; external URLs,
// `@id`, `sameAs`, `hasMap`, and image/media URLs are left alone. Resolution is: crawl inventory
// first (free), then a live `getPageIdByUrl` (authoritative) with a per-call cache. Returns the
// list of stripped URLs. Mutates `block` in place.
async function pruneUnrealUrls(
  block: Record<string, unknown>, siteHost: string, siteRoot: string,
  known: Set<string>, cache: Map<string, boolean>, client: WPClient,
): Promise<string[]> {
  const stripped: string[] = [];
  const resolves = async (val: string): Promise<boolean> => {
    const key = normPageUrl(val, siteRoot);
    if (known.has(key)) return true;
    if (cache.has(key)) return cache.get(key)!;
    let ok = false;
    try { ok = !!(await client.getPageIdByUrl(/^https?:\/\//i.test(val) ? val : new URL(val, siteRoot).href)); } catch { ok = false; }
    cache.set(key, ok);
    if (ok) known.add(key);
    return ok;
  };
  const isInternalPageUrl = (val: string): boolean => {
    if (!val || ASSET_URL_RE.test(val)) return false;
    try {
      const abs = /^https?:\/\//i.test(val) ? val : new URL(val, siteRoot || "https://x.example").href;
      const u = new URL(abs);
      if (!/^https?:$/.test(u.protocol)) return false;
      if (!/^https?:\/\//i.test(val)) return !!siteRoot;        // relative path → internal
      return u.hostname.replace(/^www\./i, "").toLowerCase() === siteHost; // absolute → same host only
    } catch { return false; }
  };
  const walk = async (node: unknown): Promise<void> => {
    if (Array.isArray(node)) { for (const n of node) await walk(n); return; }
    if (!node || typeof node !== "object") return;
    const o = node as Record<string, unknown>;
    for (const [k, v] of Object.entries(o)) {
      if (k.toLowerCase() === "url" && typeof v === "string" && isInternalPageUrl(v)) {
        if (!(await resolves(v))) { delete o[k]; stripped.push(v); continue; }
      } else {
        await walk(v);
      }
    }
  };
  await walk(block);
  return stripped;
}

// Summarize the structured data ALREADY on a page, split by SOURCE for the Schema tab's
// "already on this page" line: types the tool stored via the LSG plugin (`_audit_schema`) vs. types
// coming from the theme or another SEO plugin (live-rendered JSON-LD that isn't in `_audit_schema`).
// Best-effort — returns null if the page can't be resolved or read.
export interface ExistingSchemaSummary { lsgTypes: string[]; otherTypes: string[] }

// Per-page cache so the two lazy consumers of this lookup — the Schema tab's "Already on this page"
// line AND the up-front "Manual Update" prediction (willSchemaFixBeManual) — share ONE network read
// per URL instead of each fetching the page's existing schema. Successful reads are cached; failures
// (and empty homepage lookups) are evicted so a later retry can refetch. Cleared on a fresh page load.
const existingSchemaCache = new Map<string, Promise<ExistingSchemaSummary | null>>();
export async function describeExistingSchema(href: string, client: WPClient): Promise<ExistingSchemaSummary | null> {
  const key = href || "__home__";
  const hit = existingSchemaCache.get(key);
  if (hit) return hit;
  const p = (async (): Promise<ExistingSchemaSummary | null> => {
    try {
      const slug = lastPathSlug(href);
      let pageId: number | null = null;
      if (slug) pageId = await client.getPageIdByUrl(href);
      else { pageId = await client.getFrontPageId(); if (!pageId && href) pageId = await client.getPageIdByUrl(href); }

      const lsg = new Set<string>();
      if (pageId) {
        const stored = await client.getAuditSchema(pageId);
        for (const s of stored) { try { schemaTypesOf(JSON.parse(s)).forEach(t => lsg.add(t)); } catch { /* skip */ } }
      }
      const live = href ? await fetchLivePageSchema(href) : { types: [] as string[], blocks: [] as Record<string, unknown>[] };
      const lsgNorm = new Set([...lsg].map(normType));
      // Live-rendered types that aren't ours → the theme / another SEO plugin emitted them.
      const otherTypes = [...new Set(live.types)].filter(t => !lsgNorm.has(normType(t)));
      return { lsgTypes: [...lsg], otherTypes };
    } catch { return null; }
  })();
  existingSchemaCache.set(key, p);
  p.then(r => { if (!r) existingSchemaCache.delete(key); }).catch(() => existingSchemaCache.delete(key));
  return p;
}

// Predict — WITHOUT writing anything — whether approving a schema card will be a MANUAL action:
// the card would add a business/organization entity but the page already has one, so execSchemaFix
// can't auto-apply it and instead hands back a merged block to paste (the "Replace your schema
// block" popup). Used to label the card's button "Manual Update" up front, so the user sees it needs
// a manual paste before clicking. Best-effort: any lookup failure returns false (falls back to a
// normal Approve, which still detects the manual case at write time).
export async function willSchemaFixBeManual(href: string, blocks: Record<string, unknown>[], client: WPClient): Promise<boolean> {
  try {
    // Only cards that themselves add a business entity can ever collide with the page's existing one.
    const cardBusiness = Array.isArray(blocks) ? blocks.filter(isBusinessEntityBlock) : [];
    if (!cardBusiness.length) return false;
    const sum = await describeExistingSchema(href, client);
    if (!sum) return false;
    // A business entity from the theme / another SEO plugin is untouchable → always a merge (manual).
    if (sum.otherTypes.some(isBusinessEntityType)) return true;
    // The tool's OWN stored business entity of the SAME type this card provides is just re-approved
    // (execSchemaFix REPLACES it) — NOT manual. But a tool business of a DIFFERENT type is kept, so
    // the card's business block would still compete → manual.
    const cardBizTypes = new Set(cardBusiness.flatMap(schemaTypesOf).map(normType));
    return sum.lsgTypes.some(t => isBusinessEntityType(t) && !cardBizTypes.has(normType(t)));
  } catch { return false; }
}

async function execSchemaFix(fixId: string, client: WPClient): Promise<FixResult> {
  const card = document.getElementById(`oc-${fixId}`) ?? document.getElementById(fixId);
  const raw = card?.querySelector(".schema-payload")?.textContent?.trim();
  if (!raw) {
    return { ok: false, manual: true, message: "No schema data is attached to this card — follow the manual steps in the card, or re-run this tab's crawl to regenerate it." };
  }
  let parsed: unknown;
  try { parsed = JSON.parse(raw); }
  catch { return { ok: false, message: "This card's schema isn't valid JSON — re-run the Schema tab crawl and try again." }; }

  // Accept { url, blocks:[...] } | [ ...blocks ] | a single block object.
  const obj = parsed as { url?: string; blocks?: unknown[] };
  const blocks: Record<string, unknown>[] = Array.isArray(parsed)
    ? (parsed as Record<string, unknown>[])
    : Array.isArray(obj.blocks)
      ? (obj.blocks as Record<string, unknown>[])
      : schemaType(parsed) ? [parsed as Record<string, unknown>] : [];
  if (!blocks.length) return { ok: false, message: "No schema blocks found on this card." };

  // Schema.org conformance guard: fix invalid enum values, strip invented properties, and flag
  // unknown types BEFORE anything is written — so even older cards (generated before this check
  // existed) get cleaned on approve. Fail-open: if validation is unavailable, use the blocks as-is.
  try {
    const vres = await fetch("/api/validate-schema", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ blocks }) });
    const vdata = await vres.json() as { ok?: boolean; blocks?: Record<string, unknown>[] };
    if (vdata.ok && Array.isArray(vdata.blocks) && vdata.blocks.length) { blocks.length = 0; blocks.push(...vdata.blocks); }
  } catch { /* fail-open — proceed with the original blocks */ }

  // Resolve the target WordPress page from the payload URL (or the card's link).
  const href = obj.url
    ?? card?.querySelector<HTMLAnchorElement>(".visit-link")?.getAttribute("href")
    ?? card?.querySelector(".mono")?.textContent
    ?? "";
  const slug = lastPathSlug(href);
  let pageId: number | null;
  if (!slug) {
    // Static front page: try the settings endpoint (admins), then fall back to
    // matching the homepage URL to a published page (works for non-admin users).
    pageId = await client.getFrontPageId();
    if (!pageId && href) pageId = await client.getPageIdByUrl(href);
    if (!pageId) {
      return {
        ok: false,
        manual: true,
        message: "Couldn't resolve the homepage to an editable Page. If your homepage is a static Page, ensure the connected user can read it; if it's the blog/posts index (not a Page), add this schema manually.",
      };
    }
  } else {
    // Resolve STRICTLY by the exact URL supplied on the card — never by slug. A slug match can
    // land on a different page that ends in the same path segment (e.g. /services/detox/ vs
    // /detox/), which would silently write the schema to the wrong page.
    pageId = href ? await client.getPageIdByUrl(href) : null;
    if (!pageId) return { ok: false, message: `Couldn't find a published WordPress Page at the exact URL "${href}". Schema is applied only to that exact URL — verify it points to a Page (not a post/archive), or add the schema manually.` };
  }

  // Reconcile against ALL schema already on the page — existing schema is the source of truth, so
  // a blind Approve can never duplicate or introduce a conflicting entity. Three sources are merged:
  //   (1) the tool's own stored `_audit_schema`,
  //   (2) the LIVE rendered JSON-LD on the public page (RankMath / Yoast / theme — where a
  //       page's real business entity usually lives), and
  //   (3) the schema types the crawl baked into this card's payload (a fallback for when the
  //       live fetch fails).
  // A block is skipped if its @type (+@id when present) is already present, OR if it is a
  // business/organization entity and the page already has one — adding a second business entity,
  // even a different subtype (e.g. MedicalBusiness over MedicalClinic), creates competing entities.
  const existingRaw = await client.getAuditSchema(pageId);
  // Re-approving a schema card REPLACES the tool's own prior blocks for the types this card provides
  // (matched by @type), so re-approving both picks up validator fixes AND collapses any duplicates
  // the tool accumulated across runs (e.g. two MedicalWebPage nodes with different @ids). Only the
  // tool's OWN stored blocks of those types are dropped — theme/plugin schema and the tool's other
  // types are untouched, and a first-time approval is unaffected (nothing stored yet).
  const cardTypes = new Set(blocks.map(b => normType(schemaType(b))).filter(Boolean));
  const keptStoredRaw = existingRaw.filter(s => {
    try { return !cardTypes.has(normType(schemaType(JSON.parse(s) as Record<string, unknown>))); } catch { return true; }
  });
  const storedBlocks = keptStoredRaw
    .map(s => { try { return JSON.parse(s) as Record<string, unknown>; } catch { return null; } })
    .filter((b): b is Record<string, unknown> => !!b);
  const live = href ? await fetchLivePageSchema(href) : { types: [] as string[], blocks: [] as Record<string, unknown>[] };
  const bakedTypes = Array.isArray((obj as { existingTypes?: unknown }).existingTypes)
    ? ((obj as { existingTypes?: unknown[] }).existingTypes as unknown[]).map(String) : [];

  // The tool's OWN blocks are also rendered into the live page (printed from _audit_schema by the
  // plugin) and reflected in the crawl-baked types, so the dedup would treat a type we're REFRESHING
  // as "already present" and skip re-adding it — deleting the block instead of replacing it. Exclude
  // the types we just removed from stored (the tool's own) from the live/baked dedup sets; theme-only
  // types stay, so we still never duplicate the theme's schema.
  const removedTypes = new Set<string>();
  for (const s of existingRaw) { try { const t = normType(schemaType(JSON.parse(s) as Record<string, unknown>)); if (cardTypes.has(t)) removedTypes.add(t); } catch { /* ignore */ } }
  const liveDedupBlocks = live.blocks.filter(b => !removedTypes.has(normType(schemaType(b))));
  const liveDedupTypes = live.types.filter(t => !removedTypes.has(normType(t)));
  const bakedDedupTypes = bakedTypes.filter(t => !removedTypes.has(normType(t)));

  const existingBlocks = [...storedBlocks, ...liveDedupBlocks];
  const existingTypeSet = new Set<string>(
    [...existingBlocks.flatMap(schemaTypesOf), ...liveDedupTypes, ...bakedDedupTypes].map(normType).filter(Boolean),
  );
  const existingBusinessNames = [...new Set(
    [...existingBlocks.flatMap(schemaTypesOf), ...liveDedupTypes, ...bakedDedupTypes].filter(isBusinessEntityType),
  )];
  const pageHasBusiness = existingBusinessNames.length > 0;

  const skippedDup: string[] = [];
  const skippedRedundant: string[] = [];
  const redundantBlocks: Record<string, unknown>[] = []; // the business enhancements that were skipped
  const toAdd = blocks.filter(b => {
    const primary = schemaType(b), primaryN = normType(primary), id = schemaId(b);
    // Same @type (+@id when the block carries one) already present anywhere → duplicate.
    const dup = existingBlocks.some(e => normType(schemaType(e)) === primaryN && (id ? schemaId(e) === id : true))
      || (!id && existingTypeSet.has(primaryN));
    if (dup) { skippedDup.push(primary || "(untyped)"); return false; }
    // Competing business/organization entity → skip (the page already has one).
    if (isBusinessEntityBlock(b) && pageHasBusiness) { skippedRedundant.push(primary || "(business entity)"); redundantBlocks.push(b); return false; }
    return true;
  });

  const dupNote = skippedDup.length ? `already present: ${[...new Set(skippedDup)].join(", ")}` : "";
  const redundantNote = skippedRedundant.length
    ? `skipped to avoid a second business entity (page already has ${existingBusinessNames.join(", ")}): ${[...new Set(skippedRedundant)].join(", ")}`
    : "";

  // A recommended business enhancement was blocked (it would duplicate the existing entity). Rather
  // than silently skip, deep-merge it INTO the existing business node and hand the user the combined
  // block to paste over their original — see mergedSchema in FixResult. Requires the full existing
  // object (only available from stored/live blocks, not the crawl's baked type list).
  let mergedSchema: string | undefined;
  if (redundantBlocks.length) {
    const existingBiz = existingBlocks.filter(isBusinessEntityBlock);
    if (existingBiz.length) {
      const enhType = normType(schemaType(redundantBlocks[0]));
      const target = existingBiz.find(e => normType(schemaType(e)) === enhType)
        ?? existingBiz.find(e => schemaTypesOf(e).map(normType).includes(enhType))
        ?? existingBiz[0];
      let merged: Record<string, unknown> = { ...target };
      for (const enh of redundantBlocks) merged = deepMergeSchema(merged, enh);
      if (JSON.stringify(merged) !== JSON.stringify(target)) mergedSchema = JSON.stringify(merged, null, 2);
    }
  }

  if (!toAdd.length) {
    if (mergedSchema) {
      return {
        ok: true, manual: true, mergedSchema,
        message: `${href || "The homepage"} already has a business entity (${existingBusinessNames.join(", ")}). Approving this card would add a competing duplicate, so it was not applied. Instead, replace your existing business-schema block with the merged version below — it combines your current schema with the recommended enhancements, with no duplicates.`,
      };
    }
    const why = [dupNote, redundantNote].filter(Boolean).join("; ");
    return { ok: true, message: `No schema added to ${href || "the homepage"} — ${why || "everything is already in place"}.` };
  }

  // Verify every internal, navigable `url` inside the blocks about to be written against real pages
  // (crawl inventory first, then a live page lookup) and STRIP any that doesn't resolve — an Approve
  // must never inject a fabricated page URL. External URLs, @id, sameAs, hasMap and media URLs are
  // left untouched. The block itself is still added, minus the unreal url.
  let siteHost = "", siteRoot = "";
  try { const u = new URL(href); siteHost = u.hostname.replace(/^www\./i, "").toLowerCase(); siteRoot = u.origin; } catch { /* homepage/relative — leave blank */ }
  const strippedUrls: string[] = [];
  if (siteRoot) {
    const known = new Set<string>([normPageUrl(href, siteRoot), ...crawledPageUrls().map(u => normPageUrl(u, siteRoot))]);
    const cache = new Map<string, boolean>();
    for (const b of toAdd) strippedUrls.push(...await pruneUnrealUrls(b, siteHost, siteRoot, known, cache, client));
  }
  const urlNote = strippedUrls.length
    ? `removed ${strippedUrls.length} unverified URL${strippedUrls.length > 1 ? "s" : ""} (no matching page): ${[...new Set(strippedUrls)].join(", ")}`
    : "";

  const next = [...keptStoredRaw, ...toAdd.map(b => JSON.stringify(b))];
  const r = await client.setAuditSchema(pageId, next);
  if (!r.success) return { ok: false, message: `Schema write failed: ${r.error}` };

  // Read-back: when the helper isn't active, WordPress silently drops the unknown meta.
  const after = await client.getAuditSchema(pageId);
  const landed = toAdd.every(b => after.some(s => { try { return schemaType(JSON.parse(s)) === schemaType(b); } catch { return false; } }));
  if (!landed) {
    return {
      ok: false,
      manual: true,
      needsSchemaHelper: true,
      message: "Schema didn't save — the Local SEO Garage (LSG) plugin isn't active on this site (it may have been removed by a theme switch). Reinstall it, then approve again.",
    };
  }
  const types = toAdd.map(schemaType).filter(Boolean).join(", ");
  const extra = [dupNote, redundantNote, urlNote].filter(Boolean).join("; ");
  const mergeNote = mergedSchema
    ? " A business-entity enhancement couldn't be auto-applied without a duplicate — replace your existing block with the merged version shown below."
    : "";
  return { ok: true, manual: mergedSchema ? true : undefined, mergedSchema, targetUrl: href || undefined, pageId, message: `Schema added to ${href || "(homepage)"} (WordPress page #${pageId}): ${types} — injected in <head>, alongside any existing schema.${extra ? ` (${extra}.)` : ""}${mergeNote}` };
}

// ── Main executor ─────────────────────────────────────────────────

// Pages & Meta tab (um-{SLUG}) — read the editable H1 / Meta Title / Meta
// Description fields for THIS page and push them to the matching WordPress page.
async function execPagesMetaFix(fixId: string, client: WPClient, brand: string): Promise<FixResult> {
  const slug = fixId.replace(/^um-/, "");
  const val = (id: string) => ((document.getElementById(id) as HTMLInputElement | HTMLTextAreaElement | null)?.value ?? "").trim();

  // Existing-page cards have "Current" fields; new-page cards don't (they're creation recs).
  const isExisting = !!document.getElementById(`cur-${slug}-title`) || !!document.getElementById(`cur-${slug}-h1`);

  const newH1    = val(`et-${slug}-h1`);
  const newTitle = val(`et-${slug}-title`).replace(/\{\{BRAND\}\}/g, brand).replace(/\s+\|\s*$/, "").trim();
  const newDesc  = val(`et-${slug}-desc`);

  // ── New-page card → create a Page or Post in WordPress ───────────────────
  if (!isExisting) {
    if (!newH1 && !newTitle) return { ok: false, message: "Enter a Rec. H1 or Title before creating this page." };
    // Resolve the slug (and optional parent) from the card's proposed URL (.mono).
    const card = document.getElementById(fixId);
    const mono = card?.querySelector(".mono")?.textContent ?? "";
    const segs = mono.replace(/[#?].*$/, "").replace(/\/+$/, "").split("/").filter(Boolean);
    const pathSlug   = segs.pop() ?? slug;
    const parentSlug = segs.pop() ?? "";
    const tags = getTagsFromTextarea(`et-${slug}-tags`);
    const typeSel = (document.getElementById(`et-${slug}-type`) as HTMLSelectElement | null)?.value;
    const isPost = typeSel === "post" ? true
                 : typeSel === "page" ? false
                 : (tags.includes("Article") && !tags.includes("InnerPage"));
    // SEO meta for RankMath/Yoast/SEOPress via `meta`; AIOSEO via `seoMeta` after creation.
    const keys = metaKeysFor(activeSeoPlugin);
    const meta: Record<string, string> = {};
    if (keys) {
      if (newTitle) meta[keys.title]       = newTitle;
      if (newDesc)  meta[keys.description] = newDesc;
    }
    // Parent nesting applies to Pages only (WordPress posts aren't hierarchical). Resolve the
    // parent (from the URL's parent path segment) live on the connected site for Pages.
    let parentId: number | undefined;
    if (!isPost) {
      const parent = await resolveParentId(client, parentSlug);
      if (parent.error) return { ok: false, message: parent.error };
      parentId = parent.id;
    }
    return execCreatePage(client, {
      title:   newH1 || newTitle, // page title = H1 (theme renders title as H1)
      slug:    pathSlug,
      content: "",
      tags,
      parent:  parentId,
      isPost,
      meta,
      seoMeta: { title: newTitle, description: newDesc },
    });
  }

  // Tags field: detect whether the user changed it vs. the loaded baseline.
  const tagsEl       = document.getElementById(`et-${slug}-tags`) as HTMLTextAreaElement | null;
  const tagsLoaded   = tagsEl?.getAttribute("data-tags-loaded") === "true";
  const tagsBaseline = (tagsEl?.getAttribute("data-tags-baseline") ?? "").trim();
  const tagsNow      = (tagsEl?.value ?? "").trim();
  const tagsChanged  = !!tagsEl && tagsNow !== tagsBaseline;

  if (!newH1 && !newTitle && !newDesc && !tagsChanged) return { ok: false, message: "No recommended values entered for this page." };

  // Resolve the WordPress page from the card's Visit link URL → last path segment = slug.
  const card = document.getElementById(fixId);
  const href = card?.querySelector<HTMLAnchorElement>(".visit-link")?.getAttribute("href")
    ?? card?.querySelector(".mono")?.textContent ?? "";
  let pathSlug = "";
  try {
    const u = new URL(/^https?:\/\//.test(href) ? href : `https://x.example${href.startsWith("/") ? "" : "/"}${href}`);
    pathSlug = u.pathname.replace(/\/+$/, "").split("/").filter(Boolean).pop() ?? "";
  } catch {
    pathSlug = href.replace(/[#?].*$/, "").replace(/\/+$/, "").split("/").filter(Boolean).pop() ?? "";
  }
  if (!pathSlug) {
    return { ok: false, manual: true, message: "This looks like the homepage (no URL slug to resolve). Update its H1/title/description directly in WordPress." };
  }

  const page = await client.getPageBySlug(pathSlug);
  if (!page) {
    return { ok: false, message: `Couldn't find a WordPress page with slug "${pathSlug}". Update it manually or verify the URL.` };
  }

  const msgs: string[] = [];
  let anyFail = false;
  let wrote = false; // true once a real mutation lands → drives History logging (skips "unchanged" no-ops)

  // Meta title + description — written the right way for the detected SEO plugin
  // (RankMath/Yoast/SEOPress via post meta, AIOSEO via the LSG plugin route).
  if (newTitle || newDesc) {
    const r = await writeSeoMeta(client, page.id, { title: newTitle, description: newDesc });
    if (r.ok) {
      wrote = true;
      if (newTitle) msgs.push(`title → "${newTitle}"`);
      if (newDesc)  msgs.push("meta description updated");
    } else {
      anyFail = true;
      msgs.push(`meta title/description not written — ${r.error}; set these manually`);
    }
  }

  // H1 → page title. This theme renders the WordPress page title as the on-page
  // H1, so the recommended H1 is written to the page's post_title. This is
  // reliably REST-writable and works for Elementor pages (unlike content find/replace).
  if (newH1) {
    const r = await client.updatePage(page.id, { title: newH1 });
    if (r.success) { wrote = true; msgs.push(`H1 (page title) → "${newH1}"`); }
    else { anyFail = true; msgs.push(`H1/page-title update failed: ${r.error}`); }
  }

  // Tags — apply the edited Tags field. getOrCreateTags searches by name first,
  // so existing WordPress tags are REUSED (never duplicated); the page's tag set
  // is de-duplicated. When the field wasn't loaded from WP (e.g. approved before
  // tags finished loading), we only ADD — never remove — to avoid wiping tags.
  if (tagsEl && tagsChanged) {
    const desired = Array.from(new Set(getTagsFromTextarea(`et-${slug}-tags`)));
    let targetNames = desired;
    if (!tagsLoaded) {
      const curNames = page.tags.length ? await client.getTagNames(page.tags) : [];
      targetNames = Array.from(new Set([...curNames, ...desired]));
    }
    const desiredIds = Array.from(new Set(await client.getOrCreateTags(targetNames)));
    const curIds = Array.from(new Set(page.tags));
    const changed = desiredIds.length !== curIds.length || desiredIds.some(tid => !curIds.includes(tid));
    if (changed) {
      const r = await client.updatePage(page.id, { tags: desiredIds });
      if (r.success) { wrote = true; msgs.push(`tags → ${targetNames.length ? targetNames.join(", ") : "none"}`); }
      else { anyFail = true; msgs.push(`tags update failed: ${r.error}`); }
    } else {
      msgs.push("tags unchanged");
    }
  }

  const metaUrl = /^https?:\/\//.test(href) ? href : `/${pathSlug}/`;
  return { ok: !anyFail, manual: anyFail, targetUrl: wrote ? metaUrl : undefined, pageId: wrote ? page.id : undefined, message: `/${pathSlug}/: ${msgs.join(" · ")}` };
}

// ── On-Page SEO tab (op-{slug}) — apply H1 + meta title from the card's machine-readable payload ──
// The meta title writes via the plugin-aware path. The H1 is located by matching the card's CURRENT
// H1 against a writable source (post title → Elementor H1 heading widget → classic <h1>); if it
// matches none, the H1 is theme/template-driven and we return a manual notice rather than writing
// to the wrong place. So the button auto-applies the common cases and flags only true template H1s.

// Normalize an H1/title for comparison: strip tags, decode the common entities, collapse
// whitespace, lowercase. Lets us match the crawled "current H1" against a live WordPress source.
function normalizeH1(s: string): string {
  return (s || "")
    .replace(/<[^>]+>/g, " ")
    .replace(/&amp;/gi, "&").replace(/&nbsp;/gi, " ")
    .replace(/&#8217;|&rsquo;|&#39;|&apos;/gi, "'")
    .replace(/&#8211;|&ndash;/gi, "-").replace(/&#8212;|&mdash;/gi, "—")
    .replace(/&quot;|&#34;/gi, '"').replace(/&lt;/gi, "<").replace(/&gt;/gi, ">")
    .replace(/\s+/g, " ").trim().toLowerCase();
}

// Elementor node shape for heading detection (title + header_size live in settings).
interface ElHeadingNode { widgetType?: string; settings?: { title?: string; header_size?: string; [k: string]: unknown }; elements?: ElHeadingNode[]; [k: string]: unknown }
// Find the Elementor Heading widget rendered as an <h1> whose text matches the current H1.
// Returns a live reference into the tree (mutating settings.title mutates the tree in place).
function findH1HeadingWidget(nodes: ElHeadingNode[], currentH1Norm: string): ElHeadingNode | null {
  for (const n of nodes) {
    if (!n || typeof n !== "object") continue;
    if (n.widgetType === "heading" && n.settings && (n.settings.header_size ?? "").toLowerCase() === "h1"
        && normalizeH1(String(n.settings.title ?? "")) === currentH1Norm) return n;
    if (Array.isArray(n.elements)) { const f = findH1HeadingWidget(n.elements, currentH1Norm); if (f) return f; }
  }
  return null;
}

interface H1ApplyResult { applied: boolean; manual: boolean; message: string }
// Apply the recommended H1 to whichever source actually produces the page's current H1.
async function applyH1(client: WPClient, pageId: number, currentH1: string, newH1: string): Promise<H1ApplyResult> {
  const cur = normalizeH1(currentH1);
  if (!cur) return { applied: false, manual: true, message: `Couldn't confirm this page's current H1, so it can't be located automatically — set the H1 to "${newH1}" manually.` };

  // Source 1 — the post title (theme renders the title as the H1). Reliable + Elementor-safe.
  const title = await client.getTitle("pages", pageId).catch(() => "");
  if (normalizeH1(title) === cur) {
    const r = await client.updatePage(pageId, { title: newH1 });
    return r.success
      ? { applied: true, manual: false, message: `H1 (page title) → "${newH1}"` }
      : { applied: false, manual: false, message: `H1/page-title update failed: ${r.error}` };
  }

  // Source 2 — an Elementor <h1> Heading widget on the page. Edit the widget text in place + save.
  const model = await client.getBuilderModel("pages", pageId).catch(() => null);
  if (model?.builder === "elementor" && Array.isArray(model.elements)) {
    const widget = findH1HeadingWidget(model.elements as ElHeadingNode[], cur);
    if (widget?.settings) {
      widget.settings.title = newH1;
      const r = await client.saveElementorElements(pageId, model.elements);
      return r.success
        ? { applied: true, manual: false, message: `H1 (Elementor heading widget) → "${newH1}"` }
        : { applied: false, manual: false, message: `Elementor H1 save failed: ${(r.data as { message?: string })?.message ?? r.error ?? "unknown error"}` };
    }
  }

  // Source 3 — a classic <h1> in the post content. Exact find/replace.
  if (model?.builder === "classic" && typeof model.content === "string" && model.content) {
    const pattern = new RegExp(`(<h1[^>]*>)\\s*${escRe(currentH1)}\\s*(</h1>)`, "i");
    if (pattern.test(model.content)) {
      const r = await client.updateContent("pages", pageId, model.content.replace(pattern, `$1${newH1}$2`));
      return r.success
        ? { applied: true, manual: false, message: `H1 (page content) → "${newH1}"` }
        : { applied: false, manual: false, message: `H1 content update failed: ${r.error}` };
    }
  }

  // Source 4 — none matched → the H1 comes from a theme/page template, not the post title or an
  // H1 heading widget. Don't write blindly; tell the user this is the case and to fix it manually.
  return {
    applied: false, manual: true,
    message: `This page's H1 isn't pulling from the Post Title or an Elementor H1 heading widget — it's set by your theme or a page template, which this tool can't edit safely. Please update the H1 for this page manually (change it to "${newH1}").`,
  };
}

interface OpPayload { url?: string; currentH1?: string; recommendedH1?: string; currentTitle?: string; recommendedTitle?: string }
async function execOnPageFix(fixId: string, client: WPClient, brand: string): Promise<FixResult> {
  const card = typeof document !== "undefined" ? document.getElementById(`oc-${fixId}`) : null;
  const raw = card?.querySelector("script.op-payload")?.textContent?.trim();
  if (!raw) {
    return { ok: true, manual: true, message: "This card has no structured data to apply automatically — follow the manual steps in its action section. (Re-run the On-Page crawl to enable one-click apply on new cards.)" };
  }
  let p: OpPayload;
  try { p = JSON.parse(raw) as OpPayload; } catch { return { ok: false, message: "This card's saved data couldn't be read (invalid payload)." }; }

  const url = (p.url || "").trim();
  if (!url) return { ok: false, message: "This card is missing the target page URL — update the page manually." };
  const pageId = await client.getPageIdByUrl(url);
  if (!pageId) return { ok: false, message: `Couldn't find a published WordPress page at ${url}. Verify the URL, or update the page manually.` };

  const recH1 = (p.recommendedH1 || "").trim();
  const recTitle = (p.recommendedTitle || "").replace(/\{\{BRAND\}\}/g, brand).replace(/\s+\|\s*$/, "").trim();
  if (!recH1 && !recTitle) return { ok: false, message: "This card has no recommended H1 or meta title to apply." };

  const msgs: string[] = [];
  let anyManual = false, anyFail = false;

  if (recTitle) {
    const r = await writeSeoMeta(client, pageId, { title: recTitle });
    if (r.ok) msgs.push(`Meta title → "${recTitle}"`);
    else { anyFail = true; msgs.push(`meta title not written — ${r.error}`); }
  }
  if (recH1) {
    const h = await applyH1(client, pageId, p.currentH1 || "", recH1);
    msgs.push(h.message);
    if (!h.applied) { if (h.manual) anyManual = true; else anyFail = true; }
  }

  return { ok: !anyFail, manual: anyManual || anyFail, message: msgs.join(" · ") };
}

// ── Internal Links: insert the recommended link into a source page ───────────
function ilEscapeHtml(s: string): string {
  return s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}
function ilEscapeAttr(s: string): string {
  return s.replace(/&/g, "&amp;").replace(/"/g, "&quot;");
}
function ilLastSlug(urlOrPath: string): string {
  try {
    const u = new URL(/^https?:\/\//.test(urlOrPath) ? urlOrPath : `https://x.example/${urlOrPath.replace(/^\//, "")}`);
    return u.pathname.replace(/\/+$/, "").split("/").filter(Boolean).pop() ?? "";
  } catch {
    return urlOrPath.replace(/[#?].*$/, "").replace(/\/+$/, "").split("/").filter(Boolean).pop() ?? "";
  }
}

// Resolve the source page (the page we EDIT) to a WordPress page or post.
async function resolveContentTarget(client: WPClient, urlOrPath: string): Promise<{ type: "pages" | "posts"; id: number } | null> {
  if (/^https?:\/\//i.test(urlOrPath)) {
    const pid = await client.getPageIdByUrl(urlOrPath);
    if (pid) return { type: "pages", id: pid };
  }
  const slug = ilLastSlug(urlOrPath);
  if (!slug) {
    const pid = await client.getPageIdByUrl(urlOrPath); // homepage / root
    return pid ? { type: "pages", id: pid } : null;
  }
  const page = await client.getPageBySlug(slug);
  if (page) return { type: "pages", id: page.id };
  const post = await client.getPostBySlug(slug);
  if (post) return { type: "posts", id: post.id };
  return null;
}

// Call-to-action / booking / contact / nav-style copy where an internal link reads as spam. Used to
// keep BOTH existing-phrase linking and new-sentence insertion out of these blocks (reported bugs: a
// link jammed into a closing "Let {brand} help you…" CTA, and a sentence dropped into a phone-number
// "Call (888)… available 24/7, free and confidential. We'll walk you through every step." block).
const IL_CTA_RE = /\b(contact us|call (?:us|now|today)|call\s+\(?\d|get started|getting started|book (?:a|your|now)|schedule (?:a|your|now)|reach out|let [a-z0-9'&.\- ]{1,30} help|start (?:your|today)|begin your|admissions?|verify (?:your )?insurance|free (?:assessment|consultation)|free and confidential|100% confidential|walk you through|guide you through|here to help|here for you|help is (?:here|available|just)|get help (?:now|today)|speak (?:with|to) (?:a|an|our|us|someone)|talk to (?:a|an|our|us|someone)|request (?:a|your|more)|sign up|enroll|call today)\b/i;
// A phone number, or 24/7 availability, almost always marks a contact/CTA block.
const IL_PHONE_RE = /\(?\d{3}\)?[\s.‑–—-]\d{3}[\s.‑–—-]\d{4}/;
const IL_247_RE = /\b24\s*\/\s*7\b/;
function ilLooksLikeCta(text: string): boolean {
  const t = (text ?? "").trim();
  return IL_CTA_RE.test(t) || IL_PHONE_RE.test(t) || IL_247_RE.test(t);
}

// Tier 1 — link the card's EXACT anchor phrase where it already appears: its first clean,
// whole-phrase, un-linked occurrence inside real body copy (never a CTA/nav block). Only the card's
// anchor is ever linked — never a shorter or different term — so the result message always matches
// the anchor the card showed. Case-insensitive, and any run of spaces/non-breaking spaces in the
// page counts as a single space (Elementor/pasted copy often uses &nbsp; between words).
function linkExistingAnchor(root: HTMLElement, anchor: string, to: string, doc: Document): { linked: boolean; phrase?: string } {
  const words = anchor.trim().split(/\s+/).filter(Boolean);
  if (!words.length) return { linked: false };
  const re = new RegExp(
    `(^|[^a-z0-9])(${words.map(w => w.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")).join("[\\s\\u00a0]+")})(?=[^a-z0-9]|$)`,
    "i",
  );
  const walker = doc.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  let node: Node | null;
  while ((node = walker.nextNode())) {
    const el = node.parentElement as Element | null;
    if (!el) continue;
    if (el.closest("a")) continue;                              // already linked
    if (el.closest("nav, header, footer, button")) continue;   // page chrome, not body copy
    if (el.closest("h1, h2, h3, h4, h5, h6")) continue;       // never link inside a heading/subheading
    const block = el.closest("p, li, td, div") as HTMLElement | null;
    if (ilLooksLikeCta(block?.textContent ?? el.textContent ?? "")) continue; // never inside a CTA
    const txt = node.nodeValue ?? "";
    const m = re.exec(txt);
    if (!m) continue;
    const idx = m.index + m[1].length;
    const matched = m[2];
    const a = doc.createElement("a");
    a.setAttribute("href", to);
    a.textContent = matched;
    const frag = doc.createDocumentFragment();
    if (idx > 0) frag.appendChild(doc.createTextNode(txt.slice(0, idx)));
    frag.appendChild(a);
    const tail = txt.slice(idx + matched.length);
    if (tail) frag.appendChild(doc.createTextNode(tail));
    node.parentNode?.replaceChild(frag, node);
    return { linked: true, phrase: matched.replace(/[\s ]+/g, " ") };
  }
  return { linked: false };
}

const IL_STOPWORDS = new Set(["the","and","for","with","that","this","your","you","are","our","from","have","into","not","but","all","can","will","has","its","their","they","what","when","where","why","how","does","was","were","been","more","most","than","then","also","any","may","one","two","get","use"]);

// Tier 2 — add the recommended sentence (with the anchor linked) into the BEST place
// in the body, not just the end. Placement priority:
//   1. After a model-specified existing phrase (afterText) — the most natural spot it chose.
//   2. Into the most topically relevant paragraph (keyword overlap with the link's topic).
//   3. As its own paragraph in the MIDDLE of the body — never tacked onto the very end.
function insertSentence(root: HTMLElement, sentence: string, anchor: string, to: string, doc: Document, afterText = ""): boolean {
  const linkTag = `<a href="${ilEscapeAttr(to)}">${ilEscapeHtml(anchor)}</a>`;
  const idx = sentence.toLowerCase().indexOf(anchor.toLowerCase());
  const sentenceHtml = idx !== -1
    ? ilEscapeHtml(sentence.slice(0, idx)) + linkTag + ilEscapeHtml(sentence.slice(idx + anchor.length))
    : `${ilEscapeHtml(sentence)} ${linkTag}.`;

  // Append INTO a paragraph with a guaranteed single separating space (the reported bug was a
  // sentence jammed right after the prior period — "…future.ADHD often…").
  const appendInto = (p: HTMLElement) => {
    const needsSpace = !/\s$/.test(p.textContent ?? "");
    p.insertAdjacentHTML("beforeend", (needsSpace ? " " : "") + sentenceHtml);
  };

  // Real body paragraphs only — and NEVER a call-to-action / booking paragraph.
  const allParas = Array.from(root.querySelectorAll("p")).filter(p => (p.textContent ?? "").trim().length > 40);
  const paras = allParas.filter(p => !ilLooksLikeCta(p.textContent ?? ""));

  if (!paras.length) {
    // No usable body paragraph — add the sentence as its OWN paragraph in the MIDDLE (never
    // appended onto a CTA and never at the very end).
    const p = doc.createElement("p"); p.innerHTML = sentenceHtml;
    if (allParas.length) { const ref = allParas[Math.floor(allParas.length / 2)]; ref.parentNode?.insertBefore(p, ref); }
    else root.appendChild(p);
    return true;
  }

  // 1) Precise, model-chosen placement: the (non-CTA) paragraph containing the "afterText" marker.
  if (afterText) {
    const needle = afterText.trim().toLowerCase().slice(0, 120);
    if (needle) {
      const host = paras.find(p => (p.textContent ?? "").toLowerCase().includes(needle));
      if (host) { appendInto(host); return true; }
    }
  }

  // 2) Most topically relevant body paragraph (overlap of significant words with the link topic).
  const terms = new Set(
    `${anchor} ${sentence} ${ilLastSlug(to).replace(/[-_]+/g, " ")}`
      .toLowerCase().split(/[^a-z0-9]+/).filter(w => w.length > 3 && !IL_STOPWORDS.has(w)),
  );
  let best: HTMLElement | null = null, bestScore = 0;
  paras.forEach(p => {
    const seen = new Set((p.textContent ?? "").toLowerCase().split(/[^a-z0-9]+/));
    let score = 0; terms.forEach(t => { if (seen.has(t)) score++; });
    if (score > bestScore) { bestScore = score; best = p; }
  });
  if (best && bestScore > 0) { appendInto(best as HTMLElement); return true; }

  // 3) No clear topical match — its own paragraph after a MIDDLE body paragraph (never the last,
  //    never a CTA), so it doesn't read as an afterthought stuck at the end.
  const ref = paras[Math.floor((paras.length - 1) / 2)];
  const p = doc.createElement("p"); p.innerHTML = sentenceHtml;
  ref.parentNode?.insertBefore(p, ref.nextSibling);
  return true;
}

// A "new sentence" card is applied exactly as shown — the user approved that sentence, so we never
// swap it for an existing on-page phrase. (The crawl-time refine pass already upgrades cards whose
// target term is on the page to "existing" mode, so the card says so before approval.)
function ilWritesSentence(mode: string, sentence: string): boolean {
  return mode === "new" && !!sentence;
}

// Parse an HTML fragment, apply the link, and return the serialized HTML + what happened.
// "existing" cards: link the phrase in place (falling back to the sentence if the phrase is gone).
// "new" cards: insert the card's sentence — into real body copy, never a CTA/last paragraph.
// `tryExisting` is set false by the Elementor path, which already searched every widget.
function applyLinkToHtml(
  html: string, anchor: string, to: string, mode: string, sentence: string, afterText = "", tryExisting = true,
): { html: string; done: boolean; reason: string; action?: "existing" | "new"; phrase?: string } {
  const doc = new DOMParser().parseFromString(`<div id="__ilroot">${html}</div>`, "text/html");
  const root = doc.getElementById("__ilroot");
  if (!root) return { html, done: false, reason: "couldn't parse the content" };
  if (tryExisting && !ilWritesSentence(mode, sentence)) {
    const r = linkExistingAnchor(root, anchor, to, doc);
    if (r.linked) return { html: root.innerHTML, done: true, reason: "", action: "existing", phrase: r.phrase };
  }
  if (!sentence) {
    return { html, done: false, reason: mode === "existing" ? `the phrase "${anchor}" wasn't found on the page (it may have changed since the crawl — re-run the crawl to refresh this recommendation)` : "no sentence to insert" };
  }
  insertSentence(root, sentence, anchor, to, doc, afterText);
  return { html: root.innerHTML, done: true, reason: "", action: "new" };
}

// Elementor element tree (minimal shape — only the text-editor widgets we touch).
interface ElNode { widgetType?: string; settings?: { editor?: string;[k: string]: unknown }; elements?: ElNode[];[k: string]: unknown }
function collectTextWidgets(nodes: ElNode[], out: ElNode[]): void {
  for (const n of nodes) {
    if (!n || typeof n !== "object") continue;
    if (n.widgetType === "text-editor" && n.settings && typeof n.settings.editor === "string") out.push(n);
    if (Array.isArray(n.elements)) collectTextWidgets(n.elements, out);
  }
}

// A text-editor widget with a guaranteed string `editor` (the widget's body HTML). Returned
// objects are live references into the tree — mutating `.settings.editor` mutates the tree.
export type ElementorTextWidget = { settings: { editor: string;[k: string]: unknown };[k: string]: unknown };

// Public helper: collect every text-editor widget from an Elementor element tree, in document
// order. Used by the Low-Hanging KW Insertion tab to read/replace each widget's body HTML.
export function collectElementorTextWidgets(elements: unknown[]): ElementorTextWidget[] {
  const out: ElNode[] = [];
  collectTextWidgets(elements as ElNode[], out);
  return out as ElementorTextWidget[];
}
// Edit an Elementor tree in place: link an existing phrase inside a text widget,
// or append the sentence to the most relevant text widget. Never restructures the
// tree or adds widgets, so it can't break the layout.
function editElementorTree(elements: unknown[], anchor: string, to: string, mode: string, sentence: string, afterText = ""): { done: boolean; reason: string; action?: "existing" | "new"; phrase?: string } {
  const widgets: ElNode[] = [];
  collectTextWidgets(elements as ElNode[], widgets);
  if (!widgets.length) return { done: false, reason: "this Elementor page has no editable text widgets" };

  // "existing" cards: link the card's exact anchor in any widget where it appears. "new" cards skip
  // this and add the card's sentence as shown.
  if (!ilWritesSentence(mode, sentence)) {
    for (const w of widgets) {
      const editor = w.settings!.editor as string;
      const doc = new DOMParser().parseFromString(`<div id="__ilroot">${editor}</div>`, "text/html");
      const rootEl = doc.getElementById("__ilroot");
      if (!rootEl) continue;
      const r = linkExistingAnchor(rootEl, anchor, to, doc);
      if (r.linked) { w.settings!.editor = rootEl.innerHTML; return { done: true, reason: "", action: "existing", phrase: r.phrase }; }
    }
  }
  if (!sentence) return { done: false, reason: `the phrase "${anchor}" wasn't found on the page (it may have changed since the crawl — re-run the crawl to refresh this recommendation)` };

  // New sentence — into a NON-CTA widget: the widget with the model's "afterText" marker, else the
  // most topically relevant, else a MIDDLE widget. NEVER the last widget (typically the closing CTA,
  // which is exactly where the reported bad insertion landed).
  const bodyWidgets = widgets.filter(w => !ilLooksLikeCta(w.settings!.editor as string));
  const pool = bodyWidgets.length ? bodyWidgets : widgets;
  const needle = afterText.trim().toLowerCase().slice(0, 120);
  const terms = new Set(
    `${anchor} ${sentence} ${ilLastSlug(to).replace(/[-_]+/g, " ")}`
      .toLowerCase().split(/[^a-z0-9]+/).filter(w => w.length > 3 && !IL_STOPWORDS.has(w)),
  );
  const scoreOf = (w: ElNode) => {
    const seen = new Set((w.settings!.editor as string).toLowerCase().split(/[^a-z0-9]+/));
    let s = 0; terms.forEach(t => { if (seen.has(t)) s++; }); return s;
  };
  let host: ElNode | undefined;
  if (needle) host = pool.find(w => (w.settings!.editor as string).toLowerCase().includes(needle));
  if (!host) {
    let bestScore = 0;
    pool.forEach(w => { const s = scoreOf(w); if (s > bestScore) { bestScore = s; host = w; } });
  }
  if (!host) host = pool[Math.floor((pool.length - 1) / 2)]; // middle non-CTA widget, never the last
  // tryExisting=false: we already searched every widget for an existing phrase above.
  const r = applyLinkToHtml(host.settings!.editor as string, anchor, to, "new", sentence, afterText, false);
  if (r.done) { host.settings!.editor = r.html; return { done: true, reason: "", action: "new" }; }
  return { done: false, reason: "couldn't insert the sentence" };
}

// ── Write-time duplicate-link guard ─────────────────────────────────────────────────────────
// Shown when the source page already links to the target at approve time. Because a card may be
// recommended in more than one tab/tool (or the link may have been added manually) since the crawl
// that produced it, we re-check the LIVE page before writing so a blind Approve can't add a second
// link to the same target.
const IL_ALREADY_LINKED_MSG = "A link to this target from this source page already exists. It may have been added previously via a different tab/tool or manually since the last crawl and internal link recommendations. No changes will be made and the card will be marked complete.";

// Canonical form of a URL for "same page" comparison: host without www, path without a trailing
// slash, hash/query dropped, lowercased. Resolve relative inputs to absolute BEFORE calling.
function ilCanonUrl(u: string): string {
  try {
    const x = new URL(u);
    return `${x.hostname.replace(/^www\./i, "").toLowerCase()}${x.pathname.replace(/\/+$/, "").toLowerCase()}`;
  } catch {
    return (u || "").trim().replace(/[#?].*$/, "").replace(/\/+$/, "").toLowerCase();
  }
}

// True if `html` already contains an <a href> that resolves to `to`. Relative hrefs are resolved
// against the target's origin so "/detox/" matches an absolute target on the same site.
function htmlLinksToTarget(html: string, to: string): boolean {
  if (!html) return false;
  const toCanon = ilCanonUrl(to);
  let origin = "https://_"; try { origin = new URL(to).origin; } catch { /* keep default */ }
  const doc = new DOMParser().parseFromString(`<div>${html}</div>`, "text/html");
  return Array.from(doc.querySelectorAll("a[href]")).some(a => {
    const href = (a.getAttribute("href") || "").trim();
    if (!href || /^(#|mailto:|tel:|javascript:)/i.test(href)) return false;
    try { return ilCanonUrl(new URL(href, origin).href) === toCanon; } catch { return false; }
  });
}

// True if the Elementor tree already links to `to` — either an <a href> inside a text-editor
// widget, or a native Elementor link setting ("url":"…") on any widget (button/heading/etc).
function elementorTreeLinksToTarget(elements: unknown[], to: string): boolean {
  const widgets: ElNode[] = [];
  collectTextWidgets(elements as ElNode[], widgets);
  if (widgets.some(w => htmlLinksToTarget((w.settings?.editor as string) ?? "", to))) return true;
  const toCanon = ilCanonUrl(to);
  let origin = "https://_"; try { origin = new URL(to).origin; } catch { /* keep default */ }
  try {
    for (const m of (JSON.stringify(elements).match(/"url"\s*:\s*"([^"]+)"/g) ?? [])) {
      const raw = m.replace(/^"url"\s*:\s*"/, "").replace(/"$/, "").replace(/\\\//g, "/").trim();
      if (!raw || /^(#|mailto:|tel:|javascript:)/i.test(raw)) continue;
      try { if (ilCanonUrl(new URL(raw, origin).href) === toCanon) return true; } catch { /* ignore */ }
    }
  } catch { /* ignore */ }
  return false;
}

async function execInternalLinkFix(fixId: string, client: WPClient): Promise<FixResult> {
  const ac = document.getElementById(`ac-${fixId}`);
  if (!ac) return { ok: false, message: "Couldn't find this recommendation's data on the page." };
  const from     = (ac.getAttribute("data-il-from")     ?? "").trim();
  const to       = (ac.getAttribute("data-il-to")       ?? "").trim();
  const anchor   = (ac.getAttribute("data-il-anchor")   ?? "").trim();
  const mode     = (ac.getAttribute("data-il-mode")     ?? "").trim().toLowerCase();
  const sentence = (ac.getAttribute("data-il-sentence") ?? "").trim();
  const after    = (ac.getAttribute("data-il-after")    ?? "").trim();
  // No link data at all → this card isn't auto-appliable; fall back to a manual notice.
  if (!from && !to && !anchor) return { ok: true, manual: true, message: "Manual action required — follow the steps in this fix card." };
  if (!from || !to || !anchor) return { ok: false, message: "This recommendation is missing the source page, target URL, or anchor text." };

  const target = await resolveContentTarget(client, from);
  if (!target) return { ok: false, message: `Couldn't find the source page in WordPress: ${from}` };

  // Check THIS specific page's builder live — a site may use Elementor on some pages
  // and the normal editor on others, so we never assume from the site-wide builder.
  const model = await client.getBuilderModel(target.type, target.id);
  // Describe what ACTUALLY happened — an "existing" card falls back to its sentence when the phrase
  // is gone, so the label is derived from the applied action, not the mode. The anchor named is
  // always the card's own (the executor only ever links that exact phrase).
  const labelFor = (action?: "existing" | "new", _phrase?: string) =>
    action === "existing" ? `Linked the existing phrase "${anchor}"` : `Added a sentence linking "${anchor}"`;

  if (model.builder === "elementor") {
    if (!model.viaHelper || !Array.isArray(model.elements)) {
      return { ok: true, manual: true, message: `${from} is built with Elementor. Install/activate the Local SEO Garage (LSG) plugin (Settings → WP Integration) so approvals can edit Elementor pages — or add the link in the Elementor editor.` };
    }
    // Already links to the target (added earlier via another tab/tool or by hand) → don't duplicate.
    if (elementorTreeLinksToTarget(model.elements, to)) return { ok: true, message: IL_ALREADY_LINKED_MSG };
    const r = editElementorTree(model.elements, anchor, to, mode, sentence, after);
    if (!r.done) return { ok: false, message: `Couldn't update ${from}'s Elementor content — ${r.reason}. Nothing changed.` };
    const res = await client.saveElementorElements(target.id, model.elements);
    if (!res.success) return { ok: false, message: `Elementor save failed: ${(res.data as { message?: string })?.message ?? res.error ?? "unknown error"}` };
    return { ok: true, targetUrl: to, message: `${labelFor(r.action, r.phrase)} in ${from}'s Elementor content → ${to}.` };
  }

  // Classic / Gutenberg — edit post_content directly.
  const content = model.content;
  if (content === null) return { ok: false, message: `Couldn't read the body content of ${from}.` };
  if (!content.trim()) return { ok: false, message: `${from} has no editable body content (likely a page-builder layout) — add the link in the builder.` };
  // Already links to the target (added earlier via another tab/tool or by hand) → don't duplicate.
  if (htmlLinksToTarget(content, to)) return { ok: true, message: IL_ALREADY_LINKED_MSG };
  const r = applyLinkToHtml(content, anchor, to, mode, sentence, after);
  if (!r.done) return { ok: false, message: `Couldn't update ${from} — ${r.reason}. Nothing changed.` };
  const res = await client.updateContent(target.type, target.id, r.html);
  if (!res.success) return { ok: false, message: `WordPress rejected the update: ${(res.data as { message?: string })?.message ?? res.error ?? "unknown error"}` };
  return { ok: true, targetUrl: to, message: `${labelFor(r.action, r.phrase)} on ${from} → ${to}.` };
}

export async function executeFixById(fixId: string, client: WPClient, brand = "", plugin: SeoPlugin = "unknown"): Promise<FixResult> {
  activeSeoPlugin = plugin;
  try {
    // ANY card that has the rich "New Page Details" create form (its contenteditable
    // editor is present in the DOM) is a page-creation flow — route it through the full
    // create pipeline (meta gen, schema, FAQ schema, duplicate guard, LSG tag, live
    // publish), regardless of which tab or fix-id prefix it came from.
    if (typeof document !== "undefined" && document.getElementById(`cft-content-${fixId}`)) {
      return await execCreatePageFromForm(client, fixId, () => "");
    }

    // Pages & Meta tab cards are dynamic (um-{SLUG}) — handle before the static switch.
    if (fixId.startsWith("um-")) return await execPagesMetaFix(fixId, client, brand);

    // On-Page SEO tab cards (op-{slug}) apply the recommended H1 + meta title from the card's
    // machine-readable payload (H1 source: post title → Elementor heading widget → classic
    // content, else a manual notice). Entities (en-) cards stay manual.
    if (fixId.startsWith("op-")) return await execOnPageFix(fixId, client, brand);

    // Internal Links — Key Linking Opportunities rows (il-ko-{N}) AND the single-link
    // fix cards (il-{slug}) both carry data-il-* attributes and insert the link.
    if (fixId.startsWith("il-")) return await execInternalLinkFix(fixId, client);

    // Top Pages "Create" forms are dynamic (tp-{n}/tp-{slug}) — build the draft from
    // the form fields + the (Markdown) Generated Content textarea. No static fallback.
    if (fixId.startsWith("tp-")) return await execCreatePageFromForm(client, fixId, () => "");

    switch (fixId) {

      // ── GROUP 1: META TITLES ──────────────────────────────────
      case "af-mt-hp":
        return execMetaTitle(client, PID.homepage,
          "Teen Rehab in Montville, NJ | Guardian Recovery - Montville Adolescent Center");
      case "af-mt-tat":
        return execMetaTitle(client, PID.tat,
          "Teen Addiction Treatment Center NJ | Guardian Recovery - Montville Adolescent Center");
      case "af-mt-detox":
        return execMetaTitle(client, PID.detox,
          "Teen Medical Detox in NJ | Guardian Recovery - Montville Adolescent Center");
      case "af-mt-php":
        return execMetaTitle(client, PID.php_add,
          "Teen PHP in NJ | Partial Hospitalization | Guardian Recovery - Montville Adolescent Center");
      case "af-mt-iop":
        return execMetaTitle(client, PID.iop_add,
          "Teen IOP in NJ | Intensive Outpatient | Guardian Recovery - Montville Adolescent Center");
      case "af-mt-vc":
        return execMetaTitle(client, PID.vc,
          "Virtual Teen Therapy NJ | Online Counseling | Guardian Recovery - Montville Adolescent Center");

      // ── GROUP 2: H1 UPDATES ───────────────────────────────────
      case "af-h1-hp":
        return execH1(client, PID.homepage,
          "Teen Substance Use & Mental Health Treatment Center for Adolescents & Teens",
          "Teen Rehab & Addiction Treatment in Montville, NJ");
      case "af-h1-tat":
        return execH1(client, PID.tat,
          "Teen Addiction Treatment",
          "Teen Addiction Treatment Center in New Jersey");
      case "af-h1-php":
        return execH1(client, PID.php_add,
          "Partial Hospitalization Program (PHP) for Adolescent Addiction Treatment",
          "Teen Partial Hospitalization Program (PHP) in New Jersey");

      // ── GROUP 3: SCHEMA ───────────────────────────────────────
      case "af-sch-aggr":
        return appendSchema(client, PID.homepage, {
          "@context": "https://schema.org",
          "@type": "MedicalClinic",
          "@id": "https://www.guardianadolescent.com/#medicalclinic",
          "name": "Guardian Recovery Montville Adolescent Center",
          "url": "https://www.guardianadolescent.com/",
          "aggregateRating": {
            "@type": "AggregateRating",
            "ratingValue": "4.7",
            "reviewCount": "71",
            "bestRating": "5",
            "worstRating": "1",
          },
        });

      case "af-sch-svc-tat":
        return appendSchema(client, PID.tat, [
          {
            "@context": "https://schema.org",
            "@type": "Service",
            "serviceType": "Teen Addiction Treatment",
            "name": "Teen Addiction Treatment Center in New Jersey",
            "provider": { "@type": "MedicalClinic", "name": "Guardian Recovery Montville Adolescent Center", "url": "https://www.guardianadolescent.com/" },
            "audience": { "@type": "PeopleAudience", "suggestedMinAge": 13, "suggestedMaxAge": 17 },
            "areaServed": { "@type": "State", "name": "New Jersey" },
          },
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://www.guardianadolescent.com/" },
              { "@type": "ListItem", "position": 2, "name": "Teen Addiction Treatment", "item": "https://www.guardianadolescent.com/teen-addiction-treatment/" },
            ],
          },
        ]);

      case "af-sch-svc-det":
        return appendSchema(client, PID.detox, [
          {
            "@context": "https://schema.org",
            "@type": "Service",
            "serviceType": "Drug Detoxification",
            "name": "Teen Medical Detox in New Jersey",
            "provider": { "@type": "MedicalClinic", "name": "Guardian Recovery Montville Adolescent Center" },
            "audience": { "@type": "PeopleAudience", "suggestedMinAge": 13, "suggestedMaxAge": 17 },
          },
          {
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": [
              { "@type": "Question", "name": "What is teen medical detox?", "acceptedAnswer": { "@type": "Answer", "text": "Teen medical detox is a supervised withdrawal process for adolescents, safely managing physical dependency on drugs or alcohol under 24/7 medical care." } },
              { "@type": "Question", "name": "How long does teen detox take?", "acceptedAnswer": { "@type": "Answer", "text": "Most teen detox programs last 3–7 days depending on the substance and severity of dependency." } },
              { "@type": "Question", "name": "Is medical detox covered by insurance?", "acceptedAnswer": { "@type": "Answer", "text": "Yes. Most major insurance plans including Aetna, Cigna, BCBS, and Horizon cover medically necessary detox for adolescents." } },
              { "@type": "Question", "name": "Does my teen need detox before rehab?", "acceptedAnswer": { "@type": "Answer", "text": "For teens with physical dependency on alcohol, opioids, or benzodiazepines, medical detox is the recommended first step before PHP or residential programs." } },
              { "@type": "Question", "name": "Where is teen medical detox available in NJ?", "acceptedAnswer": { "@type": "Answer", "text": "Guardian Recovery Montville Adolescent Center offers teen medical detox in Montville, NJ, serving Morris County and surrounding areas." } },
            ],
          },
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://www.guardianadolescent.com/" },
              { "@type": "ListItem", "position": 2, "name": "Teen Addiction Treatment", "item": "https://www.guardianadolescent.com/teen-addiction-treatment/" },
              { "@type": "ListItem", "position": 3, "name": "Medical Detox", "item": "https://www.guardianadolescent.com/teen-addiction-treatment/medical-detoxification/" },
            ],
          },
        ]);

      case "af-sch-svc-res":
        return appendSchema(client, PID.res, [
          {
            "@context": "https://schema.org",
            "@type": "Service",
            "serviceType": "Residential Addiction Treatment",
            "name": "Teen Residential Inpatient Treatment in New Jersey",
            "provider": { "@type": "MedicalClinic", "name": "Guardian Recovery Montville Adolescent Center" },
            "audience": { "@type": "PeopleAudience", "suggestedMinAge": 13, "suggestedMaxAge": 17 },
          },
          {
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": [
              { "@type": "Question", "name": "What is teen residential treatment?", "acceptedAnswer": { "@type": "Answer", "text": "Teen residential inpatient treatment is a 24/7 structured program where adolescents live on-site while receiving intensive therapy for addiction and co-occurring mental health disorders." } },
              { "@type": "Question", "name": "How long is residential treatment for teens?", "acceptedAnswer": { "@type": "Answer", "text": "Programs typically run 28–90 days. Length depends on addiction severity, co-occurring conditions, and insurance authorization." } },
              { "@type": "Question", "name": "Will my teen keep up with school during residential?", "acceptedAnswer": { "@type": "Answer", "text": "Yes. Guardian Recovery provides academic support and coordinates with local school districts to maintain continuity during residential treatment." } },
              { "@type": "Question", "name": "What therapies are used in teen residential treatment?", "acceptedAnswer": { "@type": "Answer", "text": "CBT, DBT, family therapy, group therapy, trauma-focused care, and recreational therapy are core components of our residential program." } },
              { "@type": "Question", "name": "Is teen residential treatment covered by insurance?", "acceptedAnswer": { "@type": "Answer", "text": "Most major insurance plans cover residential treatment when medically necessary. Our team handles all insurance verification and pre-authorization." } },
            ],
          },
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://www.guardianadolescent.com/" },
              { "@type": "ListItem", "position": 2, "name": "Teen Addiction Treatment", "item": "https://www.guardianadolescent.com/teen-addiction-treatment/" },
              { "@type": "ListItem", "position": 3, "name": "Residential Inpatient", "item": "https://www.guardianadolescent.com/teen-addiction-treatment/residential-inpatient/" },
            ],
          },
        ]);

      case "af-sch-svc-php": {
        const r1 = await appendSchema(client, PID.php_add, [
          { "@context": "https://schema.org", "@type": "Service", "serviceType": "Partial Hospitalization Program", "name": "Teen PHP Addiction Treatment in NJ", "provider": { "@type": "MedicalClinic", "name": "Guardian Recovery Montville Adolescent Center" }, "audience": { "@type": "PeopleAudience", "suggestedMinAge": 13, "suggestedMaxAge": 17 } },
          { "@context": "https://schema.org", "@type": "BreadcrumbList", "itemListElement": [{ "@type": "ListItem", "position": 1, "name": "Home", "item": "https://www.guardianadolescent.com/" }, { "@type": "ListItem", "position": 2, "name": "Teen Addiction Treatment", "item": "https://www.guardianadolescent.com/teen-addiction-treatment/" }, { "@type": "ListItem", "position": 3, "name": "PHP — Addiction", "item": "https://www.guardianadolescent.com/teen-addiction-treatment/partial-hospitalization-program/" }] },
        ]);
        const r2 = await appendSchema(client, PID.php_mh, [
          { "@context": "https://schema.org", "@type": "Service", "serviceType": "Psychiatric Partial Hospitalization", "name": "Teen Mental Health PHP in NJ", "provider": { "@type": "MedicalClinic", "name": "Guardian Recovery Montville Adolescent Center" }, "audience": { "@type": "PeopleAudience", "suggestedMinAge": 13, "suggestedMaxAge": 17 } },
          { "@context": "https://schema.org", "@type": "BreadcrumbList", "itemListElement": [{ "@type": "ListItem", "position": 1, "name": "Home", "item": "https://www.guardianadolescent.com/" }, { "@type": "ListItem", "position": 2, "name": "Teen Mental Health Treatment", "item": "https://www.guardianadolescent.com/teen-mental-health-treatment/" }, { "@type": "ListItem", "position": 3, "name": "PHP — Mental Health", "item": "https://www.guardianadolescent.com/teen-mental-health-treatment/partial-hospitalization-program/" }] },
        ]);
        return { ok: r1.ok && r2.ok, message: `PHP Addiction: ${r1.message} | PHP Mental Health: ${r2.message}` };
      }

      case "af-sch-svc-iop": {
        const r1 = await appendSchema(client, PID.iop_add, [
          { "@context": "https://schema.org", "@type": "Service", "serviceType": "Intensive Outpatient Program", "name": "Teen IOP Addiction Treatment in NJ", "provider": { "@type": "MedicalClinic", "name": "Guardian Recovery Montville Adolescent Center" }, "audience": { "@type": "PeopleAudience", "suggestedMinAge": 13, "suggestedMaxAge": 17 } },
          { "@context": "https://schema.org", "@type": "BreadcrumbList", "itemListElement": [{ "@type": "ListItem", "position": 1, "name": "Home", "item": "https://www.guardianadolescent.com/" }, { "@type": "ListItem", "position": 2, "name": "Teen Addiction Treatment", "item": "https://www.guardianadolescent.com/teen-addiction-treatment/" }, { "@type": "ListItem", "position": 3, "name": "IOP — Addiction", "item": "https://www.guardianadolescent.com/teen-addiction-treatment/intensive-outpatient-program/" }] },
        ]);
        const r2 = await appendSchema(client, PID.iop_mh, [
          { "@context": "https://schema.org", "@type": "Service", "serviceType": "Psychiatric Intensive Outpatient Program", "name": "Teen Mental Health IOP in NJ", "provider": { "@type": "MedicalClinic", "name": "Guardian Recovery Montville Adolescent Center" }, "audience": { "@type": "PeopleAudience", "suggestedMinAge": 13, "suggestedMaxAge": 17 } },
          { "@context": "https://schema.org", "@type": "BreadcrumbList", "itemListElement": [{ "@type": "ListItem", "position": 1, "name": "Home", "item": "https://www.guardianadolescent.com/" }, { "@type": "ListItem", "position": 2, "name": "Teen Mental Health Treatment", "item": "https://www.guardianadolescent.com/teen-mental-health-treatment/" }, { "@type": "ListItem", "position": 3, "name": "IOP — Mental Health", "item": "https://www.guardianadolescent.com/teen-mental-health-treatment/intensive-outpatient-program/" }] },
        ]);
        return { ok: r1.ok && r2.ok, message: `IOP Addiction: ${r1.message} | IOP Mental Health: ${r2.message}` };
      }

      case "af-sch-faq":
        return appendSchema(client, PID.faq, {
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "mainEntity": [
            { "@type": "Question", "name": "What age range does Guardian Recovery treat?", "acceptedAnswer": { "@type": "Answer", "text": "Guardian Recovery Montville Adolescent Center treats teenagers ages 13–17 for addiction and co-occurring mental health conditions in Montville, NJ." } },
            { "@type": "Question", "name": "Does insurance cover teen rehab in NJ?", "acceptedAnswer": { "@type": "Answer", "text": "Yes. Most major carriers including Aetna, Cigna, BCBS, United Healthcare, and Horizon BCBS NJ cover teen rehab when medically necessary." } },
            { "@type": "Question", "name": "What is the difference between PHP and IOP for teens?", "acceptedAnswer": { "@type": "Answer", "text": "PHP offers 5–6 hours of daily treatment while IOP offers 3 hours, 3–5 days per week. PHP is for higher-acuity cases; IOP supports continued recovery with more flexibility." } },
            { "@type": "Question", "name": "How do I know if my teen needs rehab?", "acceptedAnswer": { "@type": "Answer", "text": "Signs include declining grades, withdrawal from friends and family, mood changes, secretive behavior, and continued use despite consequences. A clinical assessment determines the appropriate level of care." } },
            { "@type": "Question", "name": "Where is Guardian Recovery located?", "acceptedAnswer": { "@type": "Answer", "text": "Guardian Recovery Montville Adolescent Center is in Montville, New Jersey, serving Morris County, Essex County, Bergen County, and surrounding NJ areas." } },
          ],
        });

      // ── GROUP 4: PAGE SPEED (manual — no REST API path) ──────
      case "af-spd-elem":
        return { ok: true, manual: true, message: "Manual action required: WP Admin → Elementor → Settings → Experiments → enable 'Improved Asset Loading'. Then Settings → Performance → enable 'External CSS'." };
      case "af-spd-rocket":
        return { ok: true, manual: true, message: "Manual action required: Install/configure WP Rocket — enable page cache, JS deferral (exclude jQuery), and lazy load images." };
      case "af-spd-img":
        return { ok: true, manual: true, message: "Manual action required: Install ShortPixel or Imagify → run bulk optimization on all uploads from WP Admin → Media." };
      case "af-spd-cdn":
        return { ok: true, manual: true, message: "Manual action required: Add site to Cloudflare free plan → enable Auto Minify (JS/CSS/HTML), set Browser Cache TTL to 4+ hours, enable Brotli." };
      case "af-spd-alt":
        return { ok: true, manual: true, message: "Manual action required: WP Admin → Media Library → add alt text to all images. Format: 'Teen [service] at Guardian Recovery Montville Adolescent Center in NJ'." };

      // ── GROUP 5: KEYWORD DENSITY (manual — requires content rewrite) ──
      case "af-kd-tat":
        return { ok: true, manual: true, message: "Manual action required: Edit Teen Addiction Hub page — target 1.5–2.5% density for 'teen addiction treatment NJ'. Add to first 100 words, 2 H2s, image alt text. Expand to 1,200+ words." };
      case "af-kd-det":
        return { ok: true, manual: true, message: "Manual action required: Edit Medical Detox page — expand ~700 → 1,200+ words. Add 'teen medical detox' to H1, first paragraph, 2 H2s, and naturally 3–4 more times." };
      case "af-kd-vc":
        return { ok: true, manual: true, message: "Manual action required: Edit Virtual Counseling page — expand ~400 → 1,000+ words. Primary keyword: 'virtual teen therapy NJ'. Add: how it works, insurance section, FAQ block (5 Q)." };

      // ── GROUP 6: INTERNAL LINKS (manual — requires contextual content edit) ──
      case "af-il-detox":
        return { ok: true, manual: true, message: "Manual action required: Edit Homepage → add a 'Levels of Care' card grid with links to all 6 LOC pages. Anchor text for detox: 'Teen Medical Detox Program in NJ'." };
      case "af-il-vc":
        return { ok: true, manual: true, message: "Manual action required: Add Virtual Counseling to WP main navigation AND homepage hero CTA. Anchor text: 'Virtual Teen Therapy NJ'." };
      case "af-il-orphans":
        return { ok: true, manual: true, message: "Manual action required: See Internal Links tab for specific from→to pairs. Add body copy links from /teen-mental-health-treatment/ and /teen-addiction-treatment/ to each of the 9 orphaned therapy sub-pages." };
      case "af-il-faq":
        return { ok: true, manual: true, message: "Manual action required: Edit FAQ page — add contextual links in answers that mention PHP, IOP, detox, ADHD, or PTSD pointing to those service pages." };

      // ── GROUP 7: NEW PAGES ────────────────────────────────────
      case "af-new-geo":   return execCreatePageFromForm(client, "af-new-geo",   geoPageContent);
      case "af-new-ins":   return execCreatePageFromForm(client, "af-new-ins",   insPageContent);
      case "af-new-adhd":  return execCreatePageFromForm(client, "af-new-adhd",  adhdPageContent);
      case "af-new-ptsd":  return execCreatePageFromForm(client, "af-new-ptsd",  ptsdPageContent);
      case "af-new-bip":   return execCreatePageFromForm(client, "af-new-bip",   bipPageContent);
      case "af-new-cross": return execCreatePageFromForm(client, "af-new-cross", crossPageContent);
      case "af-new-xan":   return execCreatePageFromForm(client, "af-new-xan",   xanPageContent);
      case "af-new-stats": return execCreatePageFromForm(client, "af-new-stats", statsPageContent);

      // ── FAQ TAB: NEW BLOG ARTICLES ────────────────────────────
      case "fq6":  return execCreatePageFromForm(client, "fq6",  crossPageContent);
      case "fq7":  return execCreatePageFromForm(client, "fq7",  xanPageContent);
      case "fq16": return execCreatePageFromForm(client, "fq16", fq16Content);
      case "fq19": return execCreatePageFromForm(client, "fq19", tpNewchAddContent);
      case "fq23": return execCreatePageFromForm(client, "fq23", tpEmbarkFamContent);
      case "fq24": return execCreatePageFromForm(client, "fq24", statsPageContent);

      // ── CONTENT GAPS: NEW PAGES ───────────────────────────────
      case "gap1":  return execCreatePageFromForm(client, "gap1",  crossPageContent);
      case "gap2":  return execCreatePageFromForm(client, "gap2",  tpEmbarkResContent);
      case "gap3":  return execCreatePageFromForm(client, "gap3",  tpEmbarkFamContent);
      case "gap4":  return execCreatePageFromForm(client, "gap4",  geoPageContent);
      case "gap5":  return execCreatePageFromForm(client, "gap5",  insPageContent);
      case "gap6":  return execCreatePageFromForm(client, "gap6",  tpMaryOutContent);
      case "gap7":  return execCreatePageFromForm(client, "gap7",  xanPageContent);
      case "gap8":  return execCreatePageFromForm(client, "gap8",  tpNewchCocContent);
      case "gap9":  return execCreatePageFromForm(client, "gap9",  statsPageContent);
      case "gap10": return execCreatePageFromForm(client, "gap10", tpMarySubContent);

      // ── TOP PAGES: COMPETITOR-INSPIRED NEW PAGES ─────────────
      // Each reads form field values so the user can review/edit before creating
      case "tp-embark-res":  return execCreatePageFromForm(client, "tp-embark-res",  tpEmbarkResContent);
      case "tp-embark-fam":  return execCreatePageFromForm(client, "tp-embark-fam",  tpEmbarkFamContent);
      case "tp-embark-tbs":  return execCreatePageFromForm(client, "tp-embark-tbs",  tpEmbarkTbsContent);
      case "tp-embark-joy":  return execCreatePageFromForm(client, "tp-embark-joy",  tpEmbarkJoyContent);
      case "tp-mary-res":    return execCreatePageFromForm(client, "tp-mary-res",    tpMaryResContent);
      case "tp-mary-sub":    return execCreatePageFromForm(client, "tp-mary-sub",    tpMarySubContent);
      case "tp-mary-out":    return execCreatePageFromForm(client, "tp-mary-out",    tpMaryOutContent);
      case "tp-newch-cross": return execCreatePageFromForm(client, "tp-newch-cross", crossPageContent);
      case "tp-newch-xan":   return execCreatePageFromForm(client, "tp-newch-xan",   xanPageContent);
      case "tp-newch-add":   return execCreatePageFromForm(client, "tp-newch-add",   tpNewchAddContent);
      case "tp-newch-coc":   return execCreatePageFromForm(client, "tp-newch-coc",   tpNewchCocContent);
      case "tp-gr-coc":      return execCreatePageFromForm(client, "tp-gr-coc",      tpGrCocContent);
      case "tp-gr-alc":      return execCreatePageFromForm(client, "tp-gr-alc",      tpGrAlcContent);
      case "tp-gr-nj":       return execCreatePageFromForm(client, "tp-gr-nj",       tpGrNjContent);

      default: {
        // Schema tab cards apply programmatically via the SEO Audit helper.
        if (fixId.startsWith("sc-")) return await execSchemaFix(fixId, client);

        // Entities tab fix cards: if the card carries a schema payload (a schema-based entity fix),
        // apply it through the SAME injector as the Schema tab (execSchemaFix). That injector
        // reconciles against ALL schema already on the page — the tool's stored _audit_schema, the
        // live-rendered JSON-LD, and the crawl-baked types — so approving here can never duplicate
        // schema the user already applied from the Schema tab (or vice versa); it dedupes, skips a
        // second business entity, and offers the merge-and-paste block when needed. Cards WITHOUT a
        // payload (pure content/entity edits) stay manual — their steps are in the card.
        if (/^en-/.test(fixId)) {
          const card = typeof document !== "undefined"
            ? (document.getElementById(`oc-${fixId}`) ?? document.getElementById(fixId))
            : null;
          if (card?.querySelector(".schema-payload")) return await execSchemaFix(fixId, client);
          return {
            ok: true,
            manual: true,
            message: `Manual action required — follow the specific steps listed in this fix card's WordPress action section.`,
          };
        }
        return { ok: false, message: `No executor registered for fix ID: ${fixId}` };
      }
    }
  } catch (err: unknown) {
    return { ok: false, message: `Unexpected error: ${err instanceof Error ? err.message : String(err)}` };
  }
}

// ── New page content ──────────────────────────────────────────────

function geoPageContent(): string {
  return `<!-- wp:heading {"level":1} -->
<h1 class="wp-block-heading">Teen Rehab in Montville, NJ — Guardian Recovery Adolescent Center</h1>
<!-- /wp:heading -->
<!-- wp:paragraph -->
<p>If you're searching for <strong>teen rehab in Montville, NJ</strong>, Guardian Recovery Montville Adolescent Center provides comprehensive addiction and mental health treatment for teenagers ages 13–17. Located in Montville, Morris County, we serve families across northern New Jersey.</p>
<!-- /wp:paragraph -->
<!-- wp:heading {"level":2} -->
<h2 class="wp-block-heading">Why Choose Teen Rehab in Montville, NJ?</h2>
<!-- /wp:heading -->
<!-- wp:paragraph -->
<p>Montville, NJ is centrally located in Morris County, making our adolescent treatment center accessible from Parsippany, Boonton, Rockaway, Lincoln Park, Pompton Lakes, Wayne, Totowa, Woodland Park, Little Falls, Fairfield, Caldwell, and throughout Essex and Bergen County.</p>
<!-- /wp:paragraph -->
<!-- wp:heading {"level":2} -->
<h2 class="wp-block-heading">Teen Rehab Programs Available in Montville, NJ</h2>
<!-- /wp:heading -->
<!-- wp:list -->
<ul><li><strong>Medical Detox</strong> — Supervised withdrawal management for teens with physical dependency</li><li><strong>Residential Inpatient</strong> — 24/7 structured care on-site in Montville</li><li><strong>Partial Hospitalization Program (PHP)</strong> — 5–6 hours of daily clinical treatment</li><li><strong>Intensive Outpatient Program (IOP)</strong> — Flexible, 3 hours per day, 3–5 days per week</li><li><strong>Virtual Counseling</strong> — Online therapy for teens across NJ</li></ul>
<!-- /wp:list -->
<!-- wp:heading {"level":2} -->
<h2 class="wp-block-heading">Serving All of Morris County and Northern NJ</h2>
<!-- /wp:heading -->
<!-- wp:paragraph -->
<p>Our Montville teen rehab center is within 30 minutes of: <strong>Parsippany</strong>, <strong>Boonton</strong>, <strong>Rockaway</strong>, <strong>Denville</strong>, <strong>Mountain Lakes</strong>, <strong>Butler</strong>, <strong>Lincoln Park</strong>, <strong>Pompton Lakes</strong>, <strong>Wayne</strong>, <strong>Totowa</strong>, <strong>Woodland Park</strong>, <strong>Little Falls</strong>, <strong>Fairfield</strong>, <strong>Caldwell</strong>, <strong>West Caldwell</strong>, <strong>North Caldwell</strong>, <strong>Verona</strong>, and <strong>Cedar Grove</strong>.</p>
<!-- /wp:paragraph -->
<!-- wp:heading {"level":2} -->
<h2 class="wp-block-heading">Insurance Accepted for Teen Rehab in NJ</h2>
<!-- /wp:heading -->
<!-- wp:paragraph -->
<p>We accept most major insurance plans for teen rehab in Montville, NJ — including Aetna, Cigna, Blue Cross Blue Shield, United Healthcare, and Horizon BCBS NJ. Call us for a free insurance verification.</p>
<!-- /wp:paragraph -->
<script type="application/ld+json">
{
  "@context": "https://schema.org",
  "@type": "MedicalClinic",
  "name": "Guardian Recovery Montville Adolescent Center",
  "url": "https://www.guardianadolescent.com/teen-rehab-montville-nj/",
  "address": { "@type": "PostalAddress", "addressLocality": "Montville", "addressRegion": "NJ", "addressCountry": "US" },
  "geo": { "@type": "GeoCoordinates", "latitude": 40.9162, "longitude": -74.3615 },
  "areaServed": [{"@type": "City", "name": "Montville"}, {"@type": "County", "name": "Morris County"}, {"@type": "State", "name": "New Jersey"}]
}
</script>`;
}

function insPageContent(): string {
  return `<!-- wp:heading {"level":1} -->
<h1 class="wp-block-heading">Does Insurance Cover Teen Rehab in NJ?</h1>
<!-- /wp:heading -->
<!-- wp:paragraph -->
<p>Yes — most major insurance plans cover teen rehab in New Jersey when treatment is medically necessary. At Guardian Recovery Montville Adolescent Center, our admissions team handles insurance verification, pre-authorization, and all billing paperwork so your family can focus on your teen's recovery.</p>
<!-- /wp:paragraph -->
<!-- wp:heading {"level":2} -->
<h2 class="wp-block-heading">Insurance Plans We Accept</h2>
<!-- /wp:heading -->
<!-- wp:list -->
<ul><li><strong>Aetna</strong> — Covers detox, residential, PHP, and IOP for adolescents</li><li><strong>Cigna</strong> — Covers all levels of teen addiction and mental health care</li><li><strong>Blue Cross Blue Shield (BCBS)</strong> — In-network for most NJ plans</li><li><strong>United Healthcare</strong> — Covers medically necessary teen rehab</li><li><strong>Horizon BCBS NJ</strong> — NJ's largest insurer, broad teen behavioral health coverage</li></ul>
<!-- /wp:list -->
<!-- wp:heading {"level":2} -->
<h2 class="wp-block-heading">What Does Insurance Cover for Teen Rehab?</h2>
<!-- /wp:heading -->
<!-- wp:paragraph -->
<p>Under the <strong>Mental Health Parity and Addiction Equity Act (MHPAEA)</strong> and the <strong>Affordable Care Act (ACA)</strong>, insurers must cover substance use disorder and mental health treatment at the same level as medical and surgical care. This means your teen's rehab — detox, residential, PHP, or IOP — is covered when clinically indicated.</p>
<!-- /wp:paragraph -->
<!-- wp:heading {"level":2} -->
<h2 class="wp-block-heading">How to Verify Your Teen's Insurance</h2>
<!-- /wp:heading -->
<!-- wp:paragraph -->
<p>Call our admissions team today. We verify benefits within hours and explain your out-of-pocket costs before treatment begins. There is no obligation.</p>
<!-- /wp:paragraph -->`;
}

function adhdPageContent(): string {
  return `<!-- wp:heading {"level":1} -->
<h1 class="wp-block-heading">Teen ADHD Treatment in New Jersey</h1>
<!-- /wp:heading -->
<!-- wp:paragraph -->
<p>ADHD is the most common co-occurring condition in teen addiction. At Guardian Recovery Montville Adolescent Center, our <strong>teen ADHD treatment in NJ</strong> integrates evidence-based therapies — including CBT, DBT, and medication management — to address both ADHD and co-occurring substance use in adolescents ages 13–17.</p>
<!-- /wp:paragraph -->
<!-- wp:heading {"level":2} -->
<h2 class="wp-block-heading">ADHD and Teen Addiction: The Connection</h2>
<!-- /wp:heading -->
<!-- wp:paragraph -->
<p>Teens with untreated ADHD are 2–3x more likely to develop a substance use disorder. Impulsivity, risk-taking, and self-medication are common drivers. Our clinical team assesses and treats ADHD and substance use simultaneously for lasting recovery outcomes.</p>
<!-- /wp:paragraph -->
<!-- wp:heading {"level":2} -->
<h2 class="wp-block-heading">How We Treat Teen ADHD in NJ</h2>
<!-- /wp:heading -->
<!-- wp:list -->
<ul><li><strong>Cognitive Behavioral Therapy (CBT)</strong> — Builds impulse control and executive function skills</li><li><strong>Dialectical Behavior Therapy (DBT)</strong> — Emotional regulation and distress tolerance</li><li><strong>Medication Management</strong> — Supervised ADHD medication with addiction-safe protocols</li><li><strong>Family Therapy</strong> — Parent coaching and family systems support</li><li><strong>Academic Support</strong> — Coordination with schools to maintain academic continuity</li></ul>
<!-- /wp:list -->
<!-- wp:heading {"level":2} -->
<h2 class="wp-block-heading">Teen ADHD Treatment Programs Available</h2>
<!-- /wp:heading -->
<!-- wp:paragraph -->
<p>We offer ADHD treatment within all levels of care: <a href="/teen-addiction-treatment/residential-inpatient/">Residential Inpatient</a>, <a href="/teen-addiction-treatment/partial-hospitalization-program/">PHP</a>, <a href="/teen-addiction-treatment/intensive-outpatient-program/">IOP</a>, and <a href="/virtual-counseling/">Virtual Counseling</a>. Insurance is accepted.</p>
<!-- /wp:paragraph -->`;
}

function ptsdPageContent(): string {
  return `<!-- wp:heading {"level":1} -->
<h1 class="wp-block-heading">Teen PTSD Treatment in New Jersey</h1>
<!-- /wp:heading -->
<!-- wp:paragraph -->
<p>Trauma and PTSD are major drivers of teen substance use. At Guardian Recovery Montville Adolescent Center, our <strong>teen PTSD treatment in NJ</strong> uses trauma-focused approaches — EMDR, trauma-focused CBT, and somatic therapies — to help adolescents ages 13–17 heal from trauma and achieve lasting recovery.</p>
<!-- /wp:paragraph -->
<!-- wp:heading {"level":2} -->
<h2 class="wp-block-heading">PTSD and Teen Addiction: Understanding the Link</h2>
<!-- /wp:heading -->
<!-- wp:paragraph -->
<p>More than 30% of teens with substance use disorders have a history of trauma. PTSD and addiction form a cycle: substances temporarily numb trauma symptoms, while addiction worsens PTSD. Treating both simultaneously is essential.</p>
<!-- /wp:paragraph -->
<!-- wp:heading {"level":2} -->
<h2 class="wp-block-heading">Teen PTSD Treatment Approaches at Guardian Recovery</h2>
<!-- /wp:heading -->
<!-- wp:list -->
<ul><li><strong>EMDR (Eye Movement Desensitization and Reprocessing)</strong> — Trauma processing with clinically proven outcomes</li><li><strong>Trauma-Focused CBT (TF-CBT)</strong> — The gold standard for adolescent trauma treatment</li><li><strong>Somatic Therapy</strong> — Body-based trauma release techniques</li><li><strong>DBT Skills</strong> — Distress tolerance and emotional regulation</li><li><strong>Family Trauma Therapy</strong> — Healing the family system alongside the teen</li></ul>
<!-- /wp:list -->
<!-- wp:heading {"level":2} -->
<h2 class="wp-block-heading">Get Help for Teen PTSD in NJ</h2>
<!-- /wp:heading -->
<!-- wp:paragraph -->
<p>Our clinical team includes trauma-certified therapists. PTSD treatment is available within <a href="/teen-addiction-treatment/residential-inpatient/">Residential</a>, <a href="/teen-mental-health-treatment/partial-hospitalization-program/">PHP</a>, and <a href="/teen-mental-health-treatment/intensive-outpatient-program/">IOP</a> programs. Most insurance accepted.</p>
<!-- /wp:paragraph -->`;
}

function bipPageContent(): string {
  return `<!-- wp:heading {"level":1} -->
<h1 class="wp-block-heading">Teen Bipolar Disorder Treatment in New Jersey</h1>
<!-- /wp:heading -->
<!-- wp:paragraph -->
<p>Bipolar disorder is one of the most commonly misdiagnosed and undertreated conditions in adolescents. At Guardian Recovery Montville Adolescent Center, our <strong>teen bipolar disorder treatment in NJ</strong> provides accurate diagnosis, mood stabilization, and integrated care for co-occurring substance use — all tailored for adolescents ages 13–17.</p>
<!-- /wp:paragraph -->
<!-- wp:heading {"level":2} -->
<h2 class="wp-block-heading">Bipolar Disorder and Teen Substance Use</h2>
<!-- /wp:heading -->
<!-- wp:paragraph -->
<p>Teens with bipolar disorder are at significantly higher risk for substance use disorder. The impulsivity of manic episodes and the hopelessness of depressive episodes both drive substance use as a coping mechanism. Treating both conditions simultaneously is the only path to stable recovery.</p>
<!-- /wp:paragraph -->
<!-- wp:heading {"level":2} -->
<h2 class="wp-block-heading">How We Treat Teen Bipolar Disorder</h2>
<!-- /wp:heading -->
<!-- wp:list -->
<ul><li><strong>Psychiatric Evaluation and Diagnosis</strong> — Comprehensive dual-diagnosis assessment</li><li><strong>Medication Management</strong> — Evidence-based mood stabilizers and addiction-safe protocols</li><li><strong>CBT for Bipolar</strong> — Managing mood cycles and building coping skills</li><li><strong>Family Psychoeducation</strong> — Helping families understand and support their teen</li><li><strong>Relapse Prevention Planning</strong> — Specific strategies for bipolar-driven relapse triggers</li></ul>
<!-- /wp:list -->
<!-- wp:heading {"level":2} -->
<h2 class="wp-block-heading">Teen Bipolar Treatment Programs in NJ</h2>
<!-- /wp:heading -->
<!-- wp:paragraph -->
<p>Bipolar disorder treatment is integrated into our <a href="/teen-mental-health-treatment/partial-hospitalization-program/">Mental Health PHP</a> and <a href="/teen-mental-health-treatment/intensive-outpatient-program/">IOP</a> programs. Residential care is available for acute stabilization. Most insurance plans accepted.</p>
<!-- /wp:paragraph -->`;
}

function crossPageContent(): string {
  return `<!-- wp:heading {"level":1} -->
<h1 class="wp-block-heading">What Does "Crossfaded" Mean? Teen Drug Slang NJ Parents Should Know in 2025</h1>
<!-- /wp:heading -->
<!-- wp:paragraph -->
<p>If you've heard your teen use the word "crossfaded" and weren't sure what it meant, you're not alone. This guide explains <strong>crossfaded</strong> and 10+ other teen drug slang terms that NJ parents should know — and what to do if you're concerned about your teenager's substance use.</p>
<!-- /wp:paragraph -->
<!-- wp:heading {"level":2} -->
<h2 class="wp-block-heading">What Does Crossfaded Mean?</h2>
<!-- /wp:heading -->
<!-- wp:paragraph -->
<p><strong>Crossfaded</strong> means being simultaneously drunk (from alcohol) and high (from marijuana). The combination intensifies both substances' effects and dramatically increases the risk of nausea, vomiting, panic attacks, and loss of consciousness — especially dangerous for developing adolescent brains.</p>
<!-- /wp:paragraph -->
<!-- wp:heading {"level":2} -->
<h2 class="wp-block-heading">10 Teen Drug Slang Terms NJ Parents Should Know</h2>
<!-- /wp:heading -->
<!-- wp:list -->
<ul><li><strong>Crossfaded</strong> — Drunk and high simultaneously</li><li><strong>Dabbing / Dabs</strong> — Highly concentrated cannabis wax or oil (3–4x stronger than flower)</li><li><strong>Lean / Purple Drank</strong> — Cough syrup mixed with soda; contains codeine and promethazine</li><li><strong>Pressed</strong> — Counterfeit pills often containing fentanyl; extremely dangerous</li><li><strong>Molly / MDMA / Ecstasy</strong> — Party drug; causes dangerous overheating and dehydration</li><li><strong>Bars</strong> — Xanax (2mg bars); highly addictive benzodiazepine</li><li><strong>Carts / Vapes</strong> — THC vape cartridges; often unregulated and contain harmful additives</li><li><strong>Shrooms</strong> — Psilocybin mushrooms; hallucinogenic; increasingly used by teens</li><li><strong>Whippets</strong> — Nitrous oxide from whipped cream canisters; causes brain damage with repeated use</li><li><strong>Percs</strong> — Percocet (oxycodone); often counterfeit and laced with fentanyl</li></ul>
<!-- /wp:list -->
<!-- wp:heading {"level":2} -->
<h2 class="wp-block-heading">Warning Signs Your NJ Teen May Be Using Substances</h2>
<!-- /wp:heading -->
<!-- wp:list -->
<ul><li>Sudden change in friend group</li><li>Declining grades or school absences</li><li>Mood swings, irritability, or withdrawal from family</li><li>Bloodshot eyes, unusual smells, or altered speech</li><li>Secretive behavior, locked phone, or unexplained money</li></ul>
<!-- /wp:list -->
<!-- wp:heading {"level":2} -->
<h2 class="wp-block-heading">Teen Addiction Help in NJ</h2>
<!-- /wp:heading -->
<!-- wp:paragraph -->
<p>If you're concerned about your teenager's substance use, Guardian Recovery Montville Adolescent Center offers free assessments for NJ families. Our adolescent specialists can help you understand what level of care — if any — your teen needs. <a href="/teen-addiction-treatment/">Learn about teen addiction treatment in NJ</a>.</p>
<!-- /wp:paragraph -->`;
}

function xanPageContent(): string {
  return `<!-- wp:heading {"level":1} -->
<h1 class="wp-block-heading">Can Teenagers Take Xanax? What NJ Parents Should Know</h1>
<!-- /wp:heading -->
<!-- wp:paragraph -->
<p>Xanax (alprazolam) is one of the most prescribed benzodiazepines in the U.S. — and one of the most misused by teenagers. This guide answers the question <strong>"can teenagers take Xanax?"</strong> and explains the serious risks of teen Xanax use, dependency, and what to do if your NJ teen is misusing prescription benzodiazepines.</p>
<!-- /wp:paragraph -->
<!-- wp:heading {"level":2} -->
<h2 class="wp-block-heading">Can Teenagers Legally Take Xanax?</h2>
<!-- /wp:heading -->
<!-- wp:paragraph -->
<p>Xanax is FDA-approved for adults only — not for patients under 18. A psychiatrist may occasionally prescribe it short-term for an adolescent in a specific clinical context, but this is rare and heavily monitored. Any teen taking Xanax without a legitimate prescription is engaging in illegal drug use with serious health risks.</p>
<!-- /wp:paragraph -->
<!-- wp:heading {"level":2} -->
<h2 class="wp-block-heading">Why Is Xanax So Dangerous for Teens?</h2>
<!-- /wp:heading -->
<!-- wp:list -->
<ul><li><strong>Rapid physical dependency</strong> — Teens can become physically dependent within 2–4 weeks of daily use</li><li><strong>Cognitive effects</strong> — Benzodiazepines impair memory formation and learning in developing brains</li><li><strong>Counterfeit "bars"</strong> — Street Xanax is frequently pressed pills containing fentanyl — one pill can be fatal</li><li><strong>Dangerous withdrawal</strong> — Benzo withdrawal can cause seizures and death without medical supervision</li><li><strong>Combination risks</strong> — Mixing with alcohol or opioids dramatically increases overdose risk</li></ul>
<!-- /wp:list -->
<!-- wp:heading {"level":2} -->
<h2 class="wp-block-heading">Signs Your Teen May Be Misusing Xanax</h2>
<!-- /wp:heading -->
<!-- wp:list -->
<ul><li>Slurred speech or excessive drowsiness</li><li>Missing pills from a family prescription</li><li>Coordination problems or appearing "out of it"</li><li>Mood swings when not using</li><li>Finding pills or "bars" in their belongings</li></ul>
<!-- /wp:list -->
<!-- wp:heading {"level":2} -->
<h2 class="wp-block-heading">Teen Benzo Addiction Treatment in NJ</h2>
<!-- /wp:heading -->
<!-- wp:paragraph -->
<p>Benzodiazepine dependency requires <a href="/teen-addiction-treatment/medical-detoxification/">medically supervised detox</a> — withdrawal must never be attempted at home. Guardian Recovery Montville Adolescent Center provides teen benzo detox and full continuum addiction treatment for NJ families. <a href="/teen-addiction-treatment/">Learn more about teen addiction treatment in NJ</a>.</p>
<!-- /wp:paragraph -->`;
}

// ── Top Pages competitor-inspired content ─────────────────────

function tpEmbarkResContent(): string {
  return `<!-- wp:heading {"level":1} --><h1 class="wp-block-heading">Teen Residential Treatment Centers in NJ</h1><!-- /wp:heading -->
<!-- wp:paragraph --><p>If you're searching for <strong>teen residential treatment centers in NJ</strong>, Guardian Recovery Montville Adolescent Center provides 24/7 structured inpatient care for adolescents ages 13–17. Our residential program combines clinical therapy, academic support, and family involvement in a safe, supportive environment in Montville, New Jersey.</p><!-- /wp:paragraph -->
<!-- wp:heading {"level":2} --><h2 class="wp-block-heading">What Is Teen Residential Treatment?</h2><!-- /wp:heading -->
<!-- wp:paragraph --><p>Residential treatment centers for youth provide round-the-clock care where teens live on-site while receiving intensive therapy for substance use disorders and co-occurring mental health conditions. This level of care is appropriate when outpatient treatment has not been effective or when a teen's safety requires 24/7 supervision.</p><!-- /wp:paragraph -->
<!-- wp:heading {"level":2} --><h2 class="wp-block-heading">Our Residential Program in NJ</h2><!-- /wp:heading -->
<!-- wp:list --><ul><li>Individual therapy (CBT, DBT, trauma-focused)</li><li>Group therapy and peer support</li><li>Family therapy and parent coaching</li><li>Academic support coordinated with home school district</li><li>Medical and psychiatric oversight</li><li>Recreational and experiential therapies</li></ul><!-- /wp:list -->
<!-- wp:heading {"level":2} --><h2 class="wp-block-heading">Insurance Accepted</h2><!-- /wp:heading -->
<!-- wp:paragraph --><p>Most major insurance plans cover teen residential treatment when medically necessary. We verify benefits and handle all pre-authorization. <a href="/teen-addiction-treatment/residential-inpatient/">Learn more about our residential program</a> or call for a free assessment.</p><!-- /wp:paragraph -->`;
}

function tpEmbarkFamContent(): string {
  return `<!-- wp:heading {"level":1} --><h1 class="wp-block-heading">Dysfunctional Family Roles and Teen Addiction</h1><!-- /wp:heading -->
<!-- wp:paragraph --><p>In families affected by addiction or trauma, members often take on specific <strong>dysfunctional family roles</strong> that keep unhealthy patterns in place. Understanding these roles is a critical first step in teen addiction recovery — and in healing the family system.</p><!-- /wp:paragraph -->
<!-- wp:heading {"level":2} --><h2 class="wp-block-heading">The 6 Dysfunctional Family Roles</h2><!-- /wp:heading -->
<!-- wp:list --><ul><li><strong>The Hero</strong> — Overachieves to distract from family dysfunction; often the oldest child</li><li><strong>The Scapegoat</strong> — Blamed for family problems; often the child most likely to act out or use substances</li><li><strong>The Mascot</strong> — Uses humor to deflect tension; avoids processing real emotions</li><li><strong>The Lost Child</strong> — Withdraws and becomes invisible; often overlooked</li><li><strong>The Caretaker / Enabler</strong> — Protects the addicted family member from consequences</li><li><strong>The Addict / Identified Patient</strong> — Becomes the focus of the family's dysfunction</li></ul><!-- /wp:list -->
<!-- wp:heading {"level":2} --><h2 class="wp-block-heading">How Family Roles Drive Teen Substance Use</h2><!-- /wp:heading -->
<!-- wp:paragraph --><p>Teens in the Scapegoat or Lost Child role are at significantly higher risk for substance use. The Scapegoat acts out — often with drugs or alcohol — as a response to being blamed. The Lost Child turns to substances to cope with loneliness and invisibility. Recognizing which role your teen occupies is essential to effective treatment.</p><!-- /wp:paragraph -->
<!-- wp:heading {"level":2} --><h2 class="wp-block-heading">Family Therapy at Guardian Recovery NJ</h2><!-- /wp:heading -->
<!-- wp:paragraph --><p>Our family therapy program helps families identify and break dysfunctional roles. All levels of care include family therapy sessions. <a href="/teen-addiction-treatment/">Learn about teen addiction treatment in NJ</a>.</p><!-- /wp:paragraph -->`;
}

function tpEmbarkTbsContent(): string {
  return `<!-- wp:heading {"level":1} --><h1 class="wp-block-heading">Therapeutic Boarding Schools vs. Residential Treatment: What NJ Parents Should Know</h1><!-- /wp:heading -->
<!-- wp:paragraph --><p>When a teenager is struggling with addiction or serious mental health challenges, parents often consider both <strong>therapeutic boarding schools</strong> and residential treatment centers. Understanding the difference is critical — they serve very different clinical needs and operate under different licensing standards.</p><!-- /wp:paragraph -->
<!-- wp:heading {"level":2} --><h2 class="wp-block-heading">What Is a Therapeutic Boarding School?</h2><!-- /wp:heading -->
<!-- wp:paragraph --><p>Therapeutic boarding schools are private academic institutions that also provide some behavioral or emotional support. They are primarily educational placements — teens attend school on campus while receiving some level of counseling. They are <strong>not</strong> licensed addiction treatment facilities and are not covered by health insurance.</p><!-- /wp:paragraph -->
<!-- wp:heading {"level":2} --><h2 class="wp-block-heading">What Is a Residential Treatment Center?</h2><!-- /wp:heading -->
<!-- wp:paragraph --><p>Residential treatment centers (RTCs) are licensed clinical programs that provide intensive therapy for substance use disorders and co-occurring mental health conditions. They are staffed by licensed clinicians, psychiatrists, and medical personnel. Treatment is evidence-based and covered by most major insurance plans when medically necessary.</p><!-- /wp:paragraph -->
<!-- wp:heading {"level":2} --><h2 class="wp-block-heading">Which Is Right for My Teen?</h2><!-- /wp:heading -->
<!-- wp:list --><ul><li>If your teen has a <strong>diagnosed substance use disorder</strong> → Residential treatment center</li><li>If your teen has <strong>co-occurring mental health conditions</strong> → Residential treatment center</li><li>If your teen needs <strong>academic structure with mild behavioral support</strong> → Therapeutic boarding school may be appropriate</li><li>If <strong>insurance coverage</strong> is a factor → Residential treatment centers are covered; therapeutic boarding schools are not</li></ul><!-- /wp:list -->
<!-- wp:paragraph --><p>Guardian Recovery Montville Adolescent Center is a licensed residential treatment center serving NJ teens ages 13–17. <a href="/teen-addiction-treatment/residential-inpatient/">Learn about our residential program</a>.</p><!-- /wp:paragraph -->`;
}

function tpEmbarkJoyContent(): string {
  return `<!-- wp:heading {"level":1} --><h1 class="wp-block-heading">Joy vs. Happiness: What Teens in Recovery Need to Understand</h1><!-- /wp:heading -->
<!-- wp:paragraph --><p>One of the most powerful concepts in teen addiction recovery is the difference between <strong>joy and happiness</strong>. Substances create a counterfeit version of happiness — a chemical shortcut that bypasses the work of building real joy. Understanding this distinction helps teens in recovery build a life worth staying sober for.</p><!-- /wp:paragraph -->
<!-- wp:heading {"level":2} --><h2 class="wp-block-heading">What Is Happiness?</h2><!-- /wp:heading -->
<!-- wp:paragraph --><p>Happiness is a temporary emotional state triggered by external events — getting a good grade, seeing a friend, hearing a favorite song. It comes and goes. Substances hijack this system by triggering dopamine release directly, creating artificial happiness without the experience that normally produces it.</p><!-- /wp:paragraph -->
<!-- wp:heading {"level":2} --><h2 class="wp-block-heading">What Is Joy?</h2><!-- /wp:heading -->
<!-- wp:paragraph --><p>Joy is a deeper, more stable sense of meaning and contentment that comes from within. It is built through connection, purpose, mastery, and values — things that take time and effort. Joy is what recovery is actually building toward. It is not dependent on external circumstances or chemical state.</p><!-- /wp:paragraph -->
<!-- wp:heading {"level":2} --><h2 class="wp-block-heading">How We Teach This in Teen Recovery</h2><!-- /wp:heading -->
<!-- wp:paragraph --><p>At Guardian Recovery Montville Adolescent Center, our therapists use CBT and DBT to help teens identify their values, build meaningful connections, and develop intrinsic motivation for sobriety. <a href="/teen-mental-health-treatment/">Learn about teen mental health treatment in NJ</a>.</p><!-- /wp:paragraph -->`;
}

function tpMaryResContent(): string {
  return `<!-- wp:heading {"level":1} --><h1 class="wp-block-heading">Teen Residential Rehab in New Jersey</h1><!-- /wp:heading -->
<!-- wp:paragraph --><p>Guardian Recovery Montville Adolescent Center offers <strong>teen residential rehab in NJ</strong> for adolescents ages 13–17. Our inpatient program provides around-the-clock clinical care, family therapy, academic support, and evidence-based treatment for addiction and co-occurring mental health conditions in Montville, Morris County.</p><!-- /wp:paragraph -->
<!-- wp:heading {"level":2} --><h2 class="wp-block-heading">Why Choose Residential Rehab for Your Teen?</h2><!-- /wp:heading -->
<!-- wp:paragraph --><p>Residential rehab removes teens from the environment and triggers that drive substance use. It provides the structure, clinical intensity, and 24/7 support that outpatient programs cannot offer. It is the recommended level of care for teens with moderate-to-severe addiction, safety concerns, or failed outpatient attempts.</p><!-- /wp:paragraph -->
<!-- wp:heading {"level":2} --><h2 class="wp-block-heading">What's Included in Teen Residential Rehab</h2><!-- /wp:heading -->
<!-- wp:list --><ul><li>Comprehensive addiction and psychiatric assessment on admission</li><li>Individual therapy 5 days per week</li><li>Group therapy and peer support daily</li><li>Family therapy and parent education sessions</li><li>On-site academic tutoring coordinated with home school district</li><li>Medication management when clinically indicated</li><li>Discharge planning and step-down coordination</li></ul><!-- /wp:list -->
<!-- wp:heading {"level":2} --><h2 class="wp-block-heading">Insurance Coverage for Teen Residential Rehab in NJ</h2><!-- /wp:heading -->
<!-- wp:paragraph --><p>Most major insurance plans — including Aetna, Cigna, BCBS, United Healthcare, and Horizon BCBS NJ — cover teen residential rehab when medically necessary. Call our admissions team for a free, same-day insurance verification.</p><!-- /wp:paragraph -->`;
}

function tpMarySubContent(): string {
  return `<!-- wp:heading {"level":1} --><h1 class="wp-block-heading">How Does Suboxone Work? Teen MAT in New Jersey</h1><!-- /wp:heading -->
<!-- wp:paragraph --><p><strong>Suboxone</strong> (buprenorphine/naloxone) is the most widely used medication in <strong>Medication-Assisted Treatment (MAT)</strong> for opioid use disorder. For teens struggling with opioid addiction in NJ, understanding how Suboxone works — and when it is and isn't appropriate for adolescents — is essential for parents making treatment decisions.</p><!-- /wp:paragraph -->
<!-- wp:heading {"level":2} --><h2 class="wp-block-heading">How Does Suboxone Work?</h2><!-- /wp:heading -->
<!-- wp:paragraph --><p>Suboxone contains two active ingredients: <strong>buprenorphine</strong>, a partial opioid agonist that reduces cravings and withdrawal symptoms without producing a significant high, and <strong>naloxone</strong>, an opioid antagonist that blocks the effects of other opioids and deters misuse. Together, they stabilize brain chemistry while the patient engages in therapy.</p><!-- /wp:paragraph -->
<!-- wp:heading {"level":2} --><h2 class="wp-block-heading">Is Suboxone Appropriate for Teenagers?</h2><!-- /wp:heading -->
<!-- wp:paragraph --><p>Buprenorphine is FDA-approved for patients 16 and older with opioid use disorder. For adolescents, MAT decisions are made carefully in conjunction with a prescribing physician, the patient, and their family. When clinically indicated, Suboxone can be a life-saving bridge that stabilizes a teen enough to engage meaningfully in therapy.</p><!-- /wp:paragraph -->
<!-- wp:heading {"level":2} --><h2 class="wp-block-heading">MAT and Teen Opioid Addiction Treatment in NJ</h2><!-- /wp:heading -->
<!-- wp:paragraph --><p>At Guardian Recovery Montville Adolescent Center, medication-assisted treatment is considered as part of a comprehensive clinical assessment. <a href="/teen-addiction-treatment/medical-detoxification/">Learn about teen medical detox in NJ</a> or call for a free evaluation.</p><!-- /wp:paragraph -->`;
}

function tpMaryOutContent(): string {
  return `<!-- wp:heading {"level":1} --><h1 class="wp-block-heading">Outpatient Drug Rehab for Teenagers in NJ</h1><!-- /wp:heading -->
<!-- wp:paragraph --><p>Guardian Recovery Montville Adolescent Center offers <strong>outpatient drug rehab for teenagers in New Jersey</strong> through our IOP (Intensive Outpatient Program) and virtual counseling options. Outpatient treatment allows teens to continue living at home and attending school while receiving structured clinical care for substance use and co-occurring mental health conditions.</p><!-- /wp:paragraph -->
<!-- wp:heading {"level":2} --><h2 class="wp-block-heading">Who Is Teen Outpatient Rehab For?</h2><!-- /wp:heading -->
<!-- wp:list --><ul><li>Teens with mild-to-moderate substance use who do not require 24/7 supervision</li><li>Teens stepping down from residential or PHP levels of care</li><li>Teens whose home environment is stable and supportive of recovery</li><li>Teens who need to maintain school attendance during treatment</li></ul><!-- /wp:list -->
<!-- wp:heading {"level":2} --><h2 class="wp-block-heading">Teen Outpatient Programs Available in NJ</h2><!-- /wp:heading -->
<!-- wp:list --><ul><li><strong>IOP — Addiction</strong>: 3 hours/day, 3–5 days/week for teen substance use disorders</li><li><strong>IOP — Mental Health</strong>: Intensive outpatient for co-occurring mental health conditions</li><li><strong>Virtual Counseling</strong>: Online therapy for teens across NJ who cannot attend in person</li></ul><!-- /wp:list -->
<!-- wp:paragraph --><p>All outpatient programs accept major insurance. <a href="/teen-addiction-treatment/intensive-outpatient-program/">Learn about our teen IOP in NJ</a> or call for a free assessment.</p><!-- /wp:paragraph -->`;
}

function tpNewchAddContent(): string {
  return `<!-- wp:heading {"level":1} --><h1 class="wp-block-heading">Does Adderall Make Teens Emotionally Numb? What Parents Should Know</h1><!-- /wp:heading -->
<!-- wp:paragraph --><p>One of the most commonly reported side effects of Adderall in teenagers is <strong>emotional blunting</strong> — a flattening of emotions where teens describe feeling robotic, detached, or unable to feel happiness. For parents and teens navigating ADHD treatment, understanding Adderall's emotional side effects is essential.</p><!-- /wp:paragraph -->
<!-- wp:heading {"level":2} --><h2 class="wp-block-heading">What Is Adderall Emotional Blunting?</h2><!-- /wp:heading -->
<!-- wp:paragraph --><p>Adderall (amphetamine) increases dopamine and norepinephrine in the prefrontal cortex to improve focus. But at higher doses or in sensitive individuals, it can overstimulate dopamine pathways to a degree that leaves teens feeling flat, emotionless, or disconnected from things they used to enjoy. This is known as emotional blunting or affect blunting.</p><!-- /wp:paragraph -->
<!-- wp:heading {"level":2} --><h2 class="wp-block-heading">Signs Your Teen's Adderall Is Causing Emotional Blunting</h2><!-- /wp:heading -->
<!-- wp:list --><ul><li>"I don't feel like myself anymore"</li><li>Loss of interest in hobbies and friends</li><li>Flat affect — reduced facial expression and emotional responsiveness</li><li>Feeling robotic or like they're going through the motions</li><li>Decreased creativity or spontaneity</li></ul><!-- /wp:list -->
<!-- wp:heading {"level":2} --><h2 class="wp-block-heading">Adderall Misuse and Teen Addiction</h2><!-- /wp:heading -->
<!-- wp:paragraph --><p>When teens use Adderall without a prescription — or in higher doses than prescribed — emotional blunting is often accompanied by dependency, anxiety, and crash cycles. If your teen is misusing Adderall, Guardian Recovery Montville Adolescent Center offers specialized teen stimulant addiction treatment in NJ. <a href="/teen-addiction-treatment/">Learn more</a>.</p><!-- /wp:paragraph -->`;
}

function tpNewchCocContent(): string {
  return `<!-- wp:heading {"level":1} --><h1 class="wp-block-heading">Teen Cocaine Addiction Treatment in NJ</h1><!-- /wp:heading -->
<!-- wp:paragraph --><p>Cocaine use among teenagers in New Jersey is a serious and growing concern. At Guardian Recovery Montville Adolescent Center, our <strong>teen cocaine addiction treatment in NJ</strong> combines medical oversight, evidence-based therapy, and family support to help adolescents ages 13–17 achieve lasting recovery.</p><!-- /wp:paragraph -->
<!-- wp:heading {"level":2} --><h2 class="wp-block-heading">Warning Signs of Teen Cocaine Use</h2><!-- /wp:heading -->
<!-- wp:list --><ul><li>Sudden euphoria followed by crashes and irritability</li><li>Nosebleeds or chronic sniffling (if snorted)</li><li>Dramatically decreased appetite and weight loss</li><li>Increased energy and talkativeness, then severe fatigue</li><li>Paranoia, anxiety, or aggression</li><li>Finding drug paraphernalia (rolled bills, small bags, white residue)</li></ul><!-- /wp:list -->
<!-- wp:heading {"level":2} --><h2 class="wp-block-heading">How We Treat Teen Cocaine Addiction</h2><!-- /wp:heading -->
<!-- wp:paragraph --><p>There is no FDA-approved medication for cocaine use disorder, making therapy the cornerstone of treatment. Our approach includes CBT to address cocaine-specific triggers and thought patterns, contingency management to reinforce abstinence, and family therapy to rebuild trust and repair relationships.</p><!-- /wp:paragraph -->
<!-- wp:heading {"level":2} --><h2 class="wp-block-heading">Teen Cocaine Treatment Programs in NJ</h2><!-- /wp:heading -->
<!-- wp:paragraph --><p>Cocaine addiction treatment is available across all levels of care at Guardian Recovery — from <a href="/teen-addiction-treatment/residential-inpatient/">residential inpatient</a> to <a href="/teen-addiction-treatment/partial-hospitalization-program/">PHP</a> and <a href="/teen-addiction-treatment/intensive-outpatient-program/">IOP</a>. Most insurance accepted. Call for a free assessment.</p><!-- /wp:paragraph -->`;
}

function tpGrCocContent(): string {
  return `<!-- wp:heading {"level":1} --><h1 class="wp-block-heading">Is Cocaine an Amphetamine? Teen Drug Education</h1><!-- /wp:heading -->
<!-- wp:paragraph --><p>Parents and teens often ask: <strong>is cocaine an amphetamine?</strong> The short answer is no — cocaine and amphetamines are distinct drug classes that work differently in the brain, though both are powerful stimulants with serious addiction potential in adolescents.</p><!-- /wp:paragraph -->
<!-- wp:heading {"level":2} --><h2 class="wp-block-heading">Cocaine vs. Amphetamines: The Difference</h2><!-- /wp:heading -->
<!-- wp:list --><ul><li><strong>Cocaine</strong> is derived from the coca plant. It blocks the reuptake of dopamine, serotonin, and norepinephrine. Its effects last 15–30 minutes.</li><li><strong>Amphetamines</strong> (Adderall, meth) are synthetic stimulants that force the release of dopamine and norepinephrine. Effects last 4–12 hours depending on the form.</li><li>Both cause euphoria, increased energy, and dramatically elevated addiction risk in developing adolescent brains.</li></ul><!-- /wp:list -->
<!-- wp:heading {"level":2} --><h2 class="wp-block-heading">Why the Distinction Matters for Teen Treatment</h2><!-- /wp:heading -->
<!-- wp:paragraph --><p>Cocaine and amphetamine addiction have different treatment pathways. Cocaine use disorder has no approved pharmacotherapy — treatment is entirely therapy-based. Amphetamine use disorder may benefit from certain medication protocols. An accurate diagnosis ensures your teen gets the right treatment.</p><!-- /wp:paragraph -->
<!-- wp:paragraph --><p>If your teen is using cocaine or stimulants, <a href="/teen-addiction-treatment/">Guardian Recovery Montville Adolescent Center</a> offers specialized teen stimulant addiction treatment in NJ for adolescents ages 13–17.</p><!-- /wp:paragraph -->`;
}

function tpGrAlcContent(): string {
  return `<!-- wp:heading {"level":1} --><h1 class="wp-block-heading">Teens Mixing Alcohol and Medication: Dangerous Drug Interactions</h1><!-- /wp:heading -->
<!-- wp:paragraph --><p>One of the most dangerous and underappreciated risks for teenagers is <strong>mixing alcohol with medications</strong> — both prescription and over-the-counter. For NJ parents, understanding which drug combinations are most dangerous for teens can be life-saving.</p><!-- /wp:paragraph -->
<!-- wp:heading {"level":2} --><h2 class="wp-block-heading">Most Dangerous Alcohol + Medication Combinations for Teens</h2><!-- /wp:heading -->
<!-- wp:list --><ul><li><strong>Alcohol + Benzodiazepines (Xanax, Klonopin, Valium)</strong> — CNS depression can cause respiratory failure and death</li><li><strong>Alcohol + Opioids (Vicodin, Percocet, codeine)</strong> — Dramatically increases overdose risk</li><li><strong>Alcohol + Antibiotics (Flagyl, Tinidazole, Bactrim)</strong> — Causes severe nausea, vomiting, rapid heart rate</li><li><strong>Alcohol + Acetaminophen (Tylenol)</strong> — Even moderate alcohol use with Tylenol can cause liver damage</li><li><strong>Alcohol + ADHD medications (Adderall, Ritalin)</strong> — Masks intoxication, increasing overconsumption risk</li><li><strong>Alcohol + Antidepressants (SSRIs, MAOIs)</strong> — Worsens depression, impairs judgment, can cause serotonin syndrome</li></ul><!-- /wp:list -->
<!-- wp:heading {"level":2} --><h2 class="wp-block-heading">Teen Alcohol Addiction Treatment in NJ</h2><!-- /wp:heading -->
<!-- wp:paragraph --><p>If your teenager is combining alcohol with medications or showing signs of alcohol use disorder, Guardian Recovery Montville Adolescent Center offers comprehensive teen alcohol addiction treatment in NJ. <a href="/teen-addiction-treatment/">Learn more about our programs</a> or call for a free assessment.</p><!-- /wp:paragraph -->`;
}

function tpGrNjContent(): string {
  return `<!-- wp:heading {"level":1} --><h1 class="wp-block-heading">Teen Rehab in New Jersey | Guardian Recovery Adolescent Center</h1><!-- /wp:heading -->
<!-- wp:paragraph --><p>Guardian Recovery Montville Adolescent Center is a leading provider of <strong>teen rehab in New Jersey</strong>, offering a full continuum of addiction and mental health treatment for adolescents ages 13–17. Located in Montville, Morris County, we serve families across all of NJ — from Bergen County and Essex County to Ocean County and Mercer County.</p><!-- /wp:paragraph -->
<!-- wp:heading {"level":2} --><h2 class="wp-block-heading">Teen Rehab Programs in NJ</h2><!-- /wp:heading -->
<!-- wp:list --><ul><li><strong>Medical Detox</strong> — Supervised withdrawal for teens with physical dependency</li><li><strong>Residential Inpatient</strong> — 24/7 structured care in Montville, NJ</li><li><strong>PHP — Addiction & Mental Health</strong> — 5–6 hours/day intensive programming</li><li><strong>IOP — Addiction & Mental Health</strong> — 3 hours/day, 3–5 days/week</li><li><strong>Virtual Counseling</strong> — Online therapy for NJ teens statewide</li></ul><!-- /wp:list -->
<!-- wp:heading {"level":2} --><h2 class="wp-block-heading">Why Families Across NJ Choose Guardian Recovery</h2><!-- /wp:heading -->
<!-- wp:list --><ul><li>Joint Commission Accredited — the gold standard in behavioral health</li><li>Adolescent-only program — teens treated alongside peers, not adults</li><li>Academic support — school coordination maintained throughout treatment</li><li>Family-centered — parents are active participants, not bystanders</li><li>Insurance accepted — Aetna, Cigna, BCBS, United, Horizon BCBS NJ</li></ul><!-- /wp:list -->
<!-- wp:heading {"level":2} --><h2 class="wp-block-heading">Serving All of New Jersey</h2><!-- /wp:heading -->
<!-- wp:paragraph --><p>We accept teens from every county in NJ: Morris, Essex, Bergen, Passaic, Hudson, Union, Somerset, Middlesex, Monmouth, Ocean, Burlington, Camden, Mercer, and more. Transportation coordination is available for families outside Morris County.</p><!-- /wp:paragraph -->`;
}

function fq16Content(): string {
  return `<!-- wp:heading {"level":1} --><h1 class="wp-block-heading">What Are the Effects of Fentanyl on Teenagers?</h1><!-- /wp:heading -->
<!-- wp:paragraph --><p>Fentanyl is now the leading cause of death among American teenagers — surpassing car accidents and guns. Understanding <strong>fentanyl effects on teenagers</strong> and the unique dangers it poses to developing adolescent brains is essential knowledge for every NJ parent.</p><!-- /wp:paragraph -->
<!-- wp:heading {"level":2} --><h2 class="wp-block-heading">What Is Fentanyl?</h2><!-- /wp:heading -->
<!-- wp:paragraph --><p>Fentanyl is a synthetic opioid 50–100 times more potent than morphine. It is medically prescribed in patch or lozenge form for severe pain. The illicit fentanyl driving the teen overdose crisis is illegally manufactured and often pressed into counterfeit pills — "M30s" — that look identical to real prescription medications.</p><!-- /wp:paragraph -->
<!-- wp:heading {"level":2} --><h2 class="wp-block-heading">Fentanyl Effects on Teenagers</h2><!-- /wp:heading -->
<!-- wp:list --><ul><li><strong>Immediate euphoria</strong> followed by extreme sedation</li><li><strong>Slowed breathing</strong> — respiratory depression is the primary cause of overdose death</li><li><strong>Loss of consciousness</strong> — teens can appear to simply "fall asleep"</li><li><strong>Extreme physical dependence</strong> — develops faster in adolescent brains than in adults</li><li><strong>Severe withdrawal</strong> — flu-like symptoms within hours of last use</li></ul><!-- /wp:list -->
<!-- wp:heading {"level":2} --><h2 class="wp-block-heading">Why Fentanyl Is Especially Dangerous for Teens</h2><!-- /wp:heading -->
<!-- wp:paragraph --><p>Adolescent brains are uniquely vulnerable to opioids. The developing reward system forms strong associations with fentanyl's euphoric effects, accelerating addiction. One pill can be fatal — there is no safe dose of illicit fentanyl. In NJ, fentanyl is now detected in 75%+ of teen drug overdoses.</p><!-- /wp:paragraph -->
<!-- wp:heading {"level":2} --><h2 class="wp-block-heading">Warning Signs Your Teen May Be Using Fentanyl</h2><!-- /wp:heading -->
<!-- wp:list --><ul><li>Extreme drowsiness or "nodding off"</li><li>Pinpoint (very small) pupils</li><li>Slow, shallow breathing</li><li>Confusion or disorientation</li><li>Finding small blue or white pills (M30s) in their room</li></ul><!-- /wp:list -->
<!-- wp:heading {"level":2} --><h2 class="wp-block-heading">Teen Fentanyl Addiction Treatment in NJ</h2><!-- /wp:heading -->
<!-- wp:paragraph --><p>Fentanyl addiction requires <a href="/teen-addiction-treatment/medical-detoxification/">medically supervised detox</a> — withdrawal must never be managed at home. Guardian Recovery Montville Adolescent Center provides teen opioid treatment in NJ including detox, residential care, PHP, and IOP for adolescents ages 13–17.</p><!-- /wp:paragraph -->`;
}

function statsPageContent(): string {
  return `<!-- wp:heading {"level":1} -->
<h1 class="wp-block-heading">NJ Teen Drug Addiction Statistics 2025–2026</h1>
<!-- /wp:heading -->
<!-- wp:paragraph -->
<p>New Jersey has some of the highest rates of adolescent substance use in the Northeast. This page compiles the latest <strong>NJ teen drug statistics</strong> from CDC, SAMHSA, and NJDMHAS to help parents, educators, and policymakers understand the scope of teen addiction in New Jersey.</p>
<!-- /wp:paragraph -->
<!-- wp:heading {"level":2} -->
<h2 class="wp-block-heading">Key NJ Teen Drug Statistics</h2>
<!-- /wp:heading -->
<!-- wp:list -->
<ul><li><strong>1 in 5</strong> NJ high school students reports using marijuana in the past 30 days (NJ Youth Risk Behavior Survey)</li><li><strong>12.4%</strong> of NJ teens ages 12–17 reported past-year illicit drug use (SAMHSA, 2023)</li><li><strong>8.9%</strong> of NJ teens reported binge drinking in the past month</li><li><strong>Fentanyl</strong> is now present in 75%+ of NJ teen drug overdoses (NJDOH)</li><li><strong>Morris County</strong>, where Montville is located, saw a 34% increase in adolescent treatment admissions from 2021–2024 (NJDMHAS)</li></ul>
<!-- /wp:list -->
<!-- wp:heading {"level":2} -->
<h2 class="wp-block-heading">Most Common Substances Used by NJ Teens</h2>
<!-- /wp:heading -->
<!-- wp:list -->
<ul><li><strong>#1 — Marijuana / Cannabis</strong> (including high-potency concentrates and vapes)</li><li><strong>#2 — Alcohol</strong></li><li><strong>#3 — Prescription stimulants</strong> (Adderall, Ritalin) — often misused for studying</li><li><strong>#4 — Benzodiazepines</strong> (Xanax, Klonopin) — frequently counterfeit</li><li><strong>#5 — MDMA / Ecstasy / Molly</strong></li><li><strong>#6 — Opioids</strong> — including counterfeit pills containing fentanyl</li></ul>
<!-- /wp:list -->
<!-- wp:heading {"level":2} -->
<h2 class="wp-block-heading">NJ Teen Treatment Access: The Gap</h2>
<!-- /wp:heading -->
<!-- wp:paragraph -->
<p>Despite high need, fewer than <strong>10% of NJ teens</strong> who need substance use treatment receive it (SAMHSA). Stigma, lack of adolescent-specific programs, and limited insurance knowledge are the top barriers. Guardian Recovery Montville Adolescent Center was founded specifically to close this gap in Morris County and northern NJ.</p>
<!-- /wp:paragraph -->
<!-- wp:heading {"level":2} -->
<h2 class="wp-block-heading">Sources</h2>
<!-- /wp:heading -->
<!-- wp:list -->
<ul><li>SAMHSA National Survey on Drug Use and Health (NSDUH) 2023</li><li>CDC Youth Risk Behavior Surveillance System (YRBSS)</li><li>NJ Division of Mental Health and Addiction Services (NJDMHAS) Annual Report</li><li>NJ Department of Health (NJDOH) Overdose Data Dashboard</li><li>NJ Youth Risk Behavior Survey (YRBS)</li></ul>
<!-- /wp:list -->
<!-- wp:paragraph -->
<p><em>Last updated: May 2026. Statistics updated annually as new data is released.</em></p>
<!-- /wp:paragraph -->`;
}
